import { useState } from 'react';
import { 
  BookOpen, 
  Target, 
  Users, 
  Layers, 
  Lightbulb, 
  FileText, 
  Map, 
  DollarSign, 
  Code, 
  BookMarked,
  User,
  ChevronRight
} from 'lucide-react';

const Documentation = () => {
  const [activeSection, setActiveSection] = useState('value-proposition');

  const sections = [
    { id: 'value-proposition', label: 'Value Proposition', icon: Target },
    { id: 'target-customers', label: 'Target Customers', icon: Users },
    { id: 'sap-integration', label: 'SAP Integration Value', icon: Layers },
    { id: 'ai-ml-integrations', label: 'AI/ML Integrations', icon: Lightbulb },
    { id: 'patentable-methods', label: 'Patentable Methods', icon: FileText },
    { id: 'user-journeys', label: 'User Journeys', icon: Map },
    { id: 'pricing-strategy', label: 'Pricing Strategy', icon: DollarSign },
    { id: 'technical-architecture', label: 'Technical Architecture', icon: Code },
    { id: 'product-guide', label: 'Product Guide', icon: BookMarked },
    { id: 'user-guide', label: 'User Guide', icon: User },
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="h-full flex bg-cp-surface rounded-lg shadow-sm border border-cp-line overflow-hidden">
      {/* Left Navigation */}
      <div className="w-80 border-r border-cp-line overflow-y-auto bg-cp-inset">
        <div className="p-6 border-b border-cp-line bg-cp-surface sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <BookOpen className="h-6 w-6 text-cp-indigo" />
            <h2 className="text-xl font-bold text-cp-fg">Documentation</h2>
          </div>
          <p className="text-sm text-cp-muted mt-1">Complete platform documentation</p>
        </div>

        <nav className="p-4 space-y-1">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors
                  ${activeSection === section.id
                    ? 'bg-cp-inset text-cp-indigo border border-cp-line'
                    : 'text-cp-muted hover:bg-cp-surface2'
                  }
                `}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                <span className="font-medium flex-1">{section.label}</span>
                {activeSection === section.id && (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto p-8 space-y-16">
          
          {/* Value Proposition */}
          <section id="value-proposition" className="scroll-mt-4">
            <h1 className="text-3xl font-bold text-cp-fg mb-6 flex items-center gap-3">
              <Target className="h-8 w-8 text-cp-indigo" />
              Value Proposition
            </h1>
            
            <div className="prose max-w-none space-y-6 text-cp-muted">
              <div className="bg-gradient-to-br from-cp-inset to-cp-surface border border-cp-line rounded-lg p-6">
                <h2 className="text-2xl font-bold text-cp-fg mb-4">The Halo Promise</h2>
                <p className="text-lg leading-relaxed text-cp-fg">
                  Transform SAP customization from weeks to <span className="font-bold text-cp-indigo">90 seconds</span> with AI-powered multi-agent orchestration.
                </p>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-8">Core Value Pillars</h3>
              
              <div className="grid gap-6 md:grid-cols-2">
                <div className="bg-cp-surface border border-cp-line rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h4 className="text-xl font-bold text-cp-fg mb-3">🚀 10x Faster Development</h4>
                  <p className="text-cp-muted">
                    Automated design, coding, testing, and deployment pipeline reduces 2-4 week projects to 90 seconds.
                  </p>
                  <div className="mt-4 text-sm">
                    <span className="text-cp-crit line-through">2-4 weeks</span>
                    <span className="mx-2">→</span>
                    <span className="text-cp-ok font-bold">90 seconds</span>
                  </div>
                </div>

                <div className="bg-cp-surface border border-cp-line rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h4 className="text-xl font-bold text-cp-fg mb-3">✅ 95%+ Clean Core Compliance</h4>
                  <p className="text-cp-muted">
                    Automated validation at every step ensures SAP Clean Core principles are enforced without manual review.
                  </p>
                  <div className="mt-4">
                    <div className="flex items-center gap-2 text-sm text-cp-ok">
                      <div className="h-2 flex-1 bg-cp-inset rounded-full overflow-hidden">
                        <div className="h-full bg-cp-ok" style={{width: '95%'}}></div>
                      </div>
                      <span className="font-bold">95%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-cp-surface border border-cp-line rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h4 className="text-xl font-bold text-cp-fg mb-3">🤖 Zero-Touch Deployment</h4>
                  <p className="text-cp-muted">
                    Automated transport management across DEV → QA → PROD with health-check gating eliminates manual deployments.
                  </p>
                  <div className="mt-4 text-xs text-cp-faint font-mono">
                    DEV ✓ → QA ✓ → PROD ✓
                  </div>
                </div>

                <div className="bg-cp-surface border border-cp-line rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h4 className="text-xl font-bold text-cp-fg mb-3">💎 Production-Ready Code</h4>
                  <p className="text-cp-muted">
                    Context-aware AI generates ticket-specific code with proper error handling, not generic templates.
                  </p>
                  <div className="mt-4 text-sm text-cp-muted">
                    <span className="bg-cp-inset text-cp-indigo px-2 py-1 rounded text-xs font-mono">
                      CAP, ABAP RAP, Fiori UI5
                    </span>
                  </div>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-10">Measurable Business Impact</h3>
              
              <div className="bg-cp-inset border border-cp-line rounded-lg p-6">
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-cp-indigo mb-2">70%</div>
                    <div className="text-sm text-cp-muted">Reduction in development effort</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-cp-ok mb-2">2.5 days</div>
                    <div className="text-sm text-cp-muted">Average delivery time</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-cp-violet mb-2">100%</div>
                    <div className="text-sm text-cp-muted">Agent success rate</div>
                  </div>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-10">Why Organizations Choose Halo</h3>
              
              <ul className="space-y-3 list-none">
                <li className="flex items-start gap-3">
                  <span className="text-cp-ok font-bold">✓</span>
                  <div>
                    <strong className="text-cp-fg">SAP-Specific Intelligence:</strong>
                    <span className="text-cp-muted"> Deep knowledge of SAP modules, transactions, and best practices built into every agent</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cp-ok font-bold">✓</span>
                  <div>
                    <strong className="text-cp-fg">Multi-Agent Orchestration:</strong>
                    <span className="text-cp-muted"> 5 specialized agents (Design, Dev, Test, DevOps, Governance) working in concert</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cp-ok font-bold">✓</span>
                  <div>
                    <strong className="text-cp-fg">End-to-End Automation:</strong>
                    <span className="text-cp-muted"> From requirement analysis to production deployment with zero manual handoffs</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cp-ok font-bold">✓</span>
                  <div>
                    <strong className="text-cp-fg">Institutional Knowledge:</strong>
                    <span className="text-cp-muted"> Platform learns and improves with each customization delivered</span>
                  </div>
                </li>
              </ul>

              <div className="bg-cp-inset border-l-4 border-cp-mod p-6 mt-8">
                <h4 className="text-lg font-bold text-cp-mod mb-2">💰 ROI Guarantee</h4>
                <p className="text-cp-mod">
                  Organizations typically see <strong>5-10x ROI</strong> within the first year through reduced development costs, 
                  faster time-to-market, and improved code quality.
                </p>
              </div>
            </div>
          </section>

          {/* Target Customers */}
          <section id="target-customers" className="scroll-mt-4">
            <h1 className="text-3xl font-bold text-cp-fg mb-6 flex items-center gap-3">
              <Users className="h-8 w-8 text-cp-indigo" />
              Target Customers
            </h1>
            
            <div className="prose max-w-none space-y-6 text-cp-muted">
              <p className="text-lg text-cp-muted">
                Halo serves three primary customer segments in the SAP ecosystem:
              </p>

              <div className="space-y-6">
                {/* Primary Segment 1 */}
                <div className="bg-cp-inset border border-cp-line rounded-lg p-6">
                  <h3 className="text-2xl font-bold text-cp-fg mb-4">🏢 Enterprise SAP Customers</h3>
                  
                  <div className="grid md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <h4 className="font-semibold text-cp-fg mb-3">Profile:</h4>
                      <ul className="space-y-2 text-cp-muted text-sm">
                        <li>• Global 2000 companies</li>
                        <li>• 10,000+ employees</li>
                        <li>• Complex S/4HANA implementations</li>
                        <li>• Multiple SAP modules (FI, CO, SD, MM)</li>
                        <li>• $50M+ annual SAP spend</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-cp-fg mb-3">Pain Points:</h4>
                      <ul className="space-y-2 text-cp-muted text-sm">
                        <li>• Slow customization delivery (weeks)</li>
                        <li>• High development costs</li>
                        <li>• Clean Core compliance challenges</li>
                        <li>• Developer shortage</li>
                        <li>• Manual testing bottlenecks</li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-6 bg-cp-surface rounded-lg p-4">
                    <strong className="text-cp-fg">Value Delivered:</strong>
                    <p className="text-cp-muted text-sm mt-2">
                      70% reduction in customization costs, 10x faster delivery, guaranteed Clean Core compliance, 
                      and elimination of manual deployment errors. Typical annual savings: <strong>$2-5M</strong>.
                    </p>
                  </div>
                </div>

                {/* Primary Segment 2 */}
                <div className="bg-gradient-to-r from-cp-inset to-cp-inset border border-cp-line rounded-lg p-6">
                  <h3 className="text-2xl font-bold text-cp-ok mb-4">🤝 SAP Implementation Partners</h3>
                  
                  <div className="grid md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <h4 className="font-semibold text-cp-ok mb-3">Profile:</h4>
                      <ul className="space-y-2 text-cp-ok text-sm">
                        <li>• Global system integrators (Accenture, Deloitte, IBM)</li>
                        <li>• Boutique SAP consultancies</li>
                        <li>• 50-10,000 SAP consultants</li>
                        <li>• Delivering 10-100 projects annually</li>
                        <li>• $10M-$1B SAP services revenue</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-cp-ok mb-3">Pain Points:</h4>
                      <ul className="space-y-2 text-cp-ok text-sm">
                        <li>• High billable hour costs</li>
                        <li>• Consultant capacity constraints</li>
                        <li>• Quality variability across teams</li>
                        <li>• Knowledge loss when consultants leave</li>
                        <li>• Competitive pressure on pricing</li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-6 bg-cp-surface rounded-lg p-4">
                    <strong className="text-cp-ok">Value Delivered:</strong>
                    <p className="text-cp-ok text-sm mt-2">
                      5x consultant productivity, consistent code quality across all projects, institutional knowledge preservation, 
                      and ability to win more deals with faster delivery. Typical revenue increase: <strong>40-60%</strong>.
                    </p>
                  </div>
                </div>

                {/* Primary Segment 3 */}
                <div className="bg-cp-inset border border-cp-line rounded-lg p-6">
                  <h3 className="text-2xl font-bold text-cp-fg mb-4">👨‍💻 Independent Software Vendors (ISVs)</h3>
                  
                  <div className="grid md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <h4 className="font-semibold text-cp-fg mb-3">Profile:</h4>
                      <ul className="space-y-2 text-cp-muted text-sm">
                        <li>• SAP AppHaus partners</li>
                        <li>• BTP solution builders</li>
                        <li>• Industry vertical specialists</li>
                        <li>• 10-500 developers</li>
                        <li>• Building SAP extensions/add-ons</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-cp-fg mb-3">Pain Points:</h4>
                      <ul className="space-y-2 text-cp-muted text-sm">
                        <li>• Time-to-market pressure</li>
                        <li>• Development cost overhead</li>
                        <li>• Multi-customer customization needs</li>
                        <li>• Testing complexity across SAP versions</li>
                        <li>• SAP BTP learning curve</li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-6 bg-cp-surface rounded-lg p-4">
                    <strong className="text-cp-fg">Value Delivered:</strong>
                    <p className="text-cp-muted text-sm mt-2">
                      Accelerated feature development, automated multi-version testing, reduced engineering headcount requirements, 
                      and faster market entry. Typical time-to-market improvement: <strong>60-70%</strong>.
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-10">Market Opportunity</h3>
              
              <div className="bg-cp-inset border border-cp-line rounded-lg p-6">
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-3xl font-bold text-cp-indigo mb-2">25,000+</div>
                    <div className="text-sm text-cp-muted">S/4HANA Customers</div>
                    <div className="text-xs text-cp-faint mt-1">Addressable Market</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-cp-ok mb-2">2,400+</div>
                    <div className="text-sm text-cp-muted">SAP Partners</div>
                    <div className="text-xs text-cp-faint mt-1">Global System Integrators</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-cp-violet mb-2">$200B+</div>
                    <div className="text-sm text-cp-muted">SAP Services Market</div>
                    <div className="text-xs text-cp-faint mt-1">Annual Opportunity</div>
                  </div>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-10">Ideal Customer Profile (ICP)</h3>
              
              <div className="bg-cp-surface border-2 border-cp-line rounded-lg p-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-bold text-cp-fg mb-3 flex items-center gap-2">
                      <span className="text-cp-ok">✓</span> Must Have Characteristics
                    </h4>
                    <ul className="space-y-2 text-sm text-cp-muted">
                      <li>• Active S/4HANA implementation or migration</li>
                      <li>• 20+ customizations required annually</li>
                      <li>• In-house SAP development team OR use SI partners</li>
                      <li>• Clean Core compliance requirements</li>
                      <li>• DEV/QA/PROD landscape configured</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-cp-fg mb-3 flex items-center gap-2">
                      <span className="text-cp-indigo">+</span> Nice to Have
                    </h4>
                    <ul className="space-y-2 text-sm text-cp-muted">
                      <li>• SAP BTP adoption</li>
                      <li>• Fiori applications roadmap</li>
                      <li>• Agile development practices</li>
                      <li>• DevOps maturity</li>
                      <li>• Previous AI/automation initiatives</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-cp-inset border-l-4 border-cp-indigo p-6 mt-8">
                <h4 className="text-lg font-bold text-cp-fg mb-2">🎯 Sales Strategy</h4>
                <p className="text-cp-muted text-sm">
                  <strong>Land:</strong> Start with 1-2 pilot customizations in non-critical area.<br/>
                  <strong>Expand:</strong> Scale to full SAP development team after success.<br/>
                  <strong>Enterprise-Wide:</strong> Platform-level contract for all customization needs.
                </p>
              </div>
            </div>
          </section>

          {/* SAP Integration Value */}
          <section id="sap-integration" className="scroll-mt-4">
            <h1 className="text-3xl font-bold text-cp-fg mb-6 flex items-center gap-3">
              <Layers className="h-8 w-8 text-cp-indigo" />
              SAP Integration Value
            </h1>
            
            <div className="prose max-w-none space-y-6 text-cp-muted">
              <p className="text-lg text-cp-muted">
                Halo provides deep, native integration with the entire SAP ecosystem, making it the only AI platform 
                purpose-built for SAP development.
              </p>

              <h3 className="text-2xl font-semibold text-cp-fg">SAP Technology Coverage</h3>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-cp-surface border border-cp-line rounded-lg p-6">
                  <h4 className="text-lg font-bold text-cp-fg mb-4">S/4HANA On-Premise</h4>
                  <ul className="space-y-2 text-sm text-cp-muted">
                    <li className="flex items-start gap-2">
                      <span className="text-cp-ok mt-0.5">●</span>
                      <span><strong>ABAP RAP:</strong> Complete OData service generation with CDS views</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-ok mt-0.5">●</span>
                      <span><strong>ABAP Objects:</strong> Class-based development with proper encapsulation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-ok mt-0.5">●</span>
                      <span><strong>CDS Views:</strong> Core Data Services for data modeling</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-ok mt-0.5">●</span>
                      <span><strong>Enhancements:</strong> BADIs, User Exits, Enhancement Points</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-ok mt-0.5">●</span>
                      <span><strong>Custom Tables & Structures:</strong> Z-table design following SAP standards</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-cp-surface border border-cp-line rounded-lg p-6">
                  <h4 className="text-lg font-bold text-cp-fg mb-4">SAP BTP (Cloud)</h4>
                  <ul className="space-y-2 text-sm text-cp-muted">
                    <li className="flex items-start gap-2">
                      <span className="text-cp-indigo mt-0.5">●</span>
                      <span><strong>CAP (Node.js/Java):</strong> Cloud Application Programming model services</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-indigo mt-0.5">●</span>
                      <span><strong>SAP Fiori Elements:</strong> Annotation-driven UI generation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-indigo mt-0.5">●</span>
                      <span><strong>Cloud Foundry:</strong> Deployment automation via cf CLI</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-indigo mt-0.5">●</span>
                      <span><strong>HANA DB:</strong> HDI artifacts, calculation views, procedures</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-indigo mt-0.5">●</span>
                      <span><strong>Integration Suite:</strong> API integration patterns</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-cp-surface border border-cp-line rounded-lg p-6">
                  <h4 className="text-lg font-bold text-cp-fg mb-4">SAP Fiori / UI5</h4>
                  <ul className="space-y-2 text-sm text-cp-muted">
                    <li className="flex items-start gap-2">
                      <span className="text-cp-violet mt-0.5">●</span>
                      <span><strong>Custom Fiori Apps:</strong> Full SAPUI5 applications with controllers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-violet mt-0.5">●</span>
                      <span><strong>Fiori Launchpad:</strong> Tile configuration and navigation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-violet mt-0.5">●</span>
                      <span><strong>Responsive Design:</strong> Mobile-first, adaptive UI patterns</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-violet mt-0.5">●</span>
                      <span><strong>Fiori Design Guidelines:</strong> SAP-compliant UX patterns</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-cp-surface border border-cp-line rounded-lg p-6">
                  <h4 className="text-lg font-bold text-cp-fg mb-4">SAP ECC 6.0</h4>
                  <ul className="space-y-2 text-sm text-cp-muted">
                    <li className="flex items-start gap-2">
                      <span className="text-cp-imp mt-0.5">●</span>
                      <span><strong>Classic ABAP:</strong> Reports, Function Modules, Forms</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-imp mt-0.5">●</span>
                      <span><strong>Screen Painter:</strong> Dynpro screen development</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-imp mt-0.5">●</span>
                      <span><strong>Menu Painter:</strong> Transaction and menu configuration</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-imp mt-0.5">●</span>
                      <span><strong>RFC/BAPI:</strong> Remote function call interfaces</span>
                    </li>
                  </ul>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-10">SAP Module Expertise</h3>

              <div className="bg-cp-inset border border-cp-line rounded-lg p-6">
                <p className="text-sm text-cp-muted mb-6">
                  Halo AI agents have deep knowledge of all major SAP modules and their standard transactions:
                </p>
                
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h5 className="font-semibold text-cp-fg mb-3 text-sm">Financial Accounting (FI)</h5>
                    <ul className="text-xs text-cp-muted space-y-1">
                      <li>• GL Account Posting (FB01, FB50)</li>
                      <li>• AR/AP Management (FBL1N, FBL5N)</li>
                      <li>• Document Display (FB03)</li>
                      <li>• Period Opening/Closing (OB52)</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-semibold text-cp-fg mb-3 text-sm">Sales & Distribution (SD)</h5>
                    <ul className="text-xs text-cp-muted space-y-1">
                      <li>• Sales Order (VA01, VA02, VA03)</li>
                      <li>• Delivery Processing (VL01N, VL02N)</li>
                      <li>• Billing (VF01, VF02)</li>
                      <li>• Pricing (VK11, VK12)</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-semibold text-cp-fg mb-3 text-sm">Materials Management (MM)</h5>
                    <ul className="text-xs text-cp-muted space-y-1">
                      <li>• Purchase Order (ME21N, ME22N)</li>
                      <li>• Goods Receipt (MIGO)</li>
                      <li>• Invoice Verification (MIRO)</li>
                      <li>• Material Master (MM01, MM02)</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-semibold text-cp-fg mb-3 text-sm">Production Planning (PP)</h5>
                    <ul className="text-xs text-cp-muted space-y-1">
                      <li>• Production Order (CO01, CO02)</li>
                      <li>• BOM Management (CS01, CS02)</li>
                      <li>• Work Center (CR01, CR02)</li>
                      <li>• MRP Run (MD01, MD02)</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-semibold text-cp-fg mb-3 text-sm">Human Resources (HR)</h5>
                    <ul className="text-xs text-cp-muted space-y-1">
                      <li>• Personnel Actions (PA40)</li>
                      <li>• Payroll Processing (PC00)</li>
                      <li>• Time Management (PT60)</li>
                      <li>• Organizational Management (PPOME)</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-semibold text-cp-fg mb-3 text-sm">Controlling (CO)</h5>
                    <ul className="text-xs text-cp-muted space-y-1">
                      <li>• Cost Center Accounting (KS01, KS02)</li>
                      <li>• Internal Orders (KO01, KO02)</li>
                      <li>• Profitability Analysis (KE21N)</li>
                      <li>• Product Costing (CK11N)</li>
                    </ul>
                  </div>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-10">Clean Core Compliance Engine</h3>

              <div className="bg-cp-inset border-l-4 border-cp-ok p-6">
                <h4 className="text-lg font-bold text-cp-ok mb-3">Automated Validation at Every Step</h4>
                <p className="text-cp-ok text-sm mb-4">
                  Halo enforces SAP Clean Core principles automatically, ensuring all customizations are upgrade-safe 
                  and compliant with SAP's strategic direction.
                </p>

                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-cp-surface rounded p-4">
                    <strong className="text-cp-ok">What We Check:</strong>
                    <ul className="mt-2 space-y-1 text-cp-ok text-xs">
                      <li>✓ No modifications to SAP standard objects</li>
                      <li>✓ Use of stable APIs only</li>
                      <li>✓ Extensibility over modification</li>
                      <li>✓ Proper namespace (Z/Y) for custom objects</li>
                      <li>✓ BTP extension recommendations</li>
                    </ul>
                  </div>
                  <div className="bg-cp-surface rounded p-4">
                    <strong className="text-cp-ok">Compliance Score:</strong>
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-cp-ok">Clean Core</span>
                        <span className="font-bold text-cp-ok">95%</span>
                      </div>
                      <div className="h-2 bg-cp-inset rounded-full overflow-hidden">
                        <div className="h-full bg-cp-ok" style={{width: '95%'}}></div>
                      </div>
                      <p className="text-xs text-cp-ok mt-2">
                        Typical Halo customization compliance rate
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-10">SAP API Integration</h3>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-cp-surface border border-cp-line rounded-lg p-4">
                  <h4 className="font-semibold text-cp-fg mb-3 text-sm">Supported APIs</h4>
                  <ul className="space-y-2 text-xs text-cp-muted">
                    <li>• <strong>OData V2/V4:</strong> Service consumption and generation</li>
                    <li>• <strong>REST APIs:</strong> Custom HTTP endpoints</li>
                    <li>• <strong>RFC/BAPI:</strong> Classic remote function calls</li>
                    <li>• <strong>IDoc:</strong> EDI integration patterns</li>
                    <li>• <strong>SAP Gateway:</strong> Service enablement</li>
                    <li>• <strong>Cloud Foundry API:</strong> BTP deployment</li>
                    <li>• <strong>CTS+ API:</strong> Transport management</li>
                    <li>• <strong>ADT API:</strong> ABAP development tools</li>
                  </ul>
                </div>

                <div className="bg-cp-surface border border-cp-line rounded-lg p-4">
                  <h4 className="font-semibold text-cp-fg mb-3 text-sm">Deployment Methods</h4>
                  <ul className="space-y-2 text-xs text-cp-muted">
                    <li>• <strong>Transport Requests:</strong> Standard SAP transports (TDEVC, DEVK)</li>
                    <li>• <strong>cf push:</strong> Cloud Foundry CLI deployment</li>
                    <li>• <strong>ADT Upload:</strong> Direct ABAP code upload via Eclipse</li>
                    <li>• <strong>OData Upload:</strong> File-based deployment via APIs</li>
                    <li>• <strong>Manual Import:</strong> Guided manual process</li>
                  </ul>
                  <div className="mt-4 p-3 bg-cp-inset rounded">
                    <p className="text-xs text-cp-fg">
                      <strong>Architecture Detection:</strong> Halo automatically detects your SAP landscape 
                      architecture and selects the optimal deployment method.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-cp-inset border border-cp-line rounded-lg p-6 mt-8">
                <h4 className="text-lg font-bold text-cp-fg mb-3">🏆 Competitive Advantage</h4>
                <p className="text-cp-muted text-sm">
                  Unlike generic AI coding assistants (GitHub Copilot, Cursor), Halo is <strong>SAP-native</strong>:
                </p>
                <ul className="mt-3 space-y-2 text-sm text-cp-muted">
                  <li>• Understands SAP architecture patterns and best practices</li>
                  <li>• Generates code using correct SAP APIs and frameworks</li>
                  <li>• Enforces Clean Core compliance automatically</li>
                  <li>• Integrates with SAP transport and deployment systems</li>
                  <li>• Speaks the language of SAP developers and consultants</li>
                </ul>
              </div>
            </div>
          </section>

          {/* AI/ML Integrations */}
          <section id="ai-ml-integrations" className="scroll-mt-4">
            <h1 className="text-3xl font-bold text-cp-fg mb-6 flex items-center gap-3">
              <Lightbulb className="h-8 w-8 text-cp-indigo" />
              AI/ML Integrations
            </h1>
            
            <div className="prose max-w-none space-y-6 text-cp-muted">
              <p className="text-lg text-cp-muted">
                Halo leverages cutting-edge large language models (LLMs) from multiple providers, 
                using the Emergent LLM Universal Key for seamless multi-model access.
              </p>

              <div className="bg-cp-inset border-2 border-cp-line rounded-lg p-6 mb-8">
                <h3 className="text-xl font-bold text-cp-fg mb-3">🔑 Emergent LLM Universal Key</h3>
                <p className="text-cp-muted text-sm mb-3">
                  Single API key provides access to 12+ AI models from OpenAI, Anthropic, and Google. 
                  No need to manage multiple API accounts or keys.
                </p>
                <div className="bg-cp-surface rounded p-3 text-xs font-mono text-cp-fg">
                  sk-emergent-xxxxxxxxxxxxx
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg">Supported AI Models</h3>

              <div className="grid md:grid-cols-3 gap-6">
                {/* OpenAI */}
                <div className="bg-cp-surface border-2 border-cp-line rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-10 w-10 bg-cp-inset rounded-lg flex items-center justify-center">
                      <span className="text-cp-ok font-bold text-lg">O</span>
                    </div>
                    <h4 className="text-lg font-bold text-cp-fg">OpenAI</h4>
                  </div>
                  
                  <ul className="space-y-3 text-sm">
                    <li>
                      <div className="font-semibold text-cp-fg">GPT-5</div>
                      <div className="text-xs text-cp-muted">Most advanced model, best for complex reasoning</div>
                      <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-cp-inset text-cp-ok rounded">Recommended for DESIGN</span>
                    </li>
                    <li>
                      <div className="font-semibold text-cp-fg">GPT-4o</div>
                      <div className="text-xs text-cp-muted">Balanced performance and cost</div>
                      <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-cp-inset text-cp-indigo rounded">Default for DEV</span>
                    </li>
                    <li>
                      <div className="font-semibold text-cp-fg">GPT-4o-mini</div>
                      <div className="text-xs text-cp-muted">Fast and cost-effective</div>
                      <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-cp-surface2 text-cp-muted rounded">Used for TEST/GOVERNANCE</span>
                    </li>
                    <li>
                      <div className="font-semibold text-cp-fg">GPT-5 Mini</div>
                      <div className="text-xs text-cp-muted">Faster variant of GPT-5</div>
                    </li>
                    <li>
                      <div className="font-semibold text-cp-fg">o1, o1-mini, o1-pro</div>
                      <div className="text-xs text-cp-muted">Deep reasoning models for complex logic</div>
                      <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-cp-inset text-cp-violet rounded">Best for edge cases</span>
                    </li>
                    <li>
                      <div className="font-semibold text-cp-fg">gpt-image-1</div>
                      <div className="text-xs text-cp-muted">Image generation for UI mockups</div>
                    </li>
                  </ul>
                </div>

                {/* Anthropic */}
                <div className="bg-cp-surface border-2 border-cp-line rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-10 w-10 bg-cp-inset rounded-lg flex items-center justify-center">
                      <span className="text-cp-imp font-bold text-lg">A</span>
                    </div>
                    <h4 className="text-lg font-bold text-cp-fg">Anthropic</h4>
                  </div>
                  
                  <ul className="space-y-3 text-sm">
                    <li>
                      <div className="font-semibold text-cp-fg">Claude 4 Opus</div>
                      <div className="text-xs text-cp-muted">Maximum intelligence, 200K context window</div>
                      <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-cp-inset text-cp-imp rounded">Best for large codebases</span>
                    </li>
                    <li>
                      <div className="font-semibold text-cp-fg">Claude 4 Sonnet</div>
                      <div className="text-xs text-cp-muted">Balanced Claude variant</div>
                    </li>
                    <li>
                      <div className="font-semibold text-cp-fg">Claude 3.7</div>
                      <div className="text-xs text-cp-muted">Previous generation, still powerful</div>
                    </li>
                  </ul>

                  <div className="mt-4 p-3 bg-cp-inset rounded text-xs">
                    <strong className="text-cp-imp">Use Case:</strong>
                    <p className="text-cp-imp mt-1">
                      Claude excels at long-context analysis and governance review with its 200K token window.
                    </p>
                  </div>
                </div>

                {/* Google */}
                <div className="bg-cp-surface border-2 border-cp-line rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-10 w-10 bg-cp-inset rounded-lg flex items-center justify-center">
                      <span className="text-cp-indigo font-bold text-lg">G</span>
                    </div>
                    <h4 className="text-lg font-bold text-cp-fg">Google</h4>
                  </div>
                  
                  <ul className="space-y-3 text-sm">
                    <li>
                      <div className="font-semibold text-cp-fg">Gemini 2.5 Pro</div>
                      <div className="text-xs text-cp-muted">Most capable Gemini model</div>
                      <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-cp-inset text-cp-indigo rounded">Alternative to GPT-5</span>
                    </li>
                    <li>
                      <div className="font-semibold text-cp-fg">Gemini 2.0 Flash</div>
                      <div className="text-xs text-cp-muted">Fast multimodal model</div>
                    </li>
                  </ul>

                  <div className="mt-4 p-3 bg-cp-inset rounded text-xs">
                    <strong className="text-cp-fg">Use Case:</strong>
                    <p className="text-cp-muted mt-1">
                      Gemini's multimodal capabilities useful for analyzing UI screenshots and diagrams.
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-10">Model Selection Strategy</h3>

              <div className="bg-cp-inset border border-cp-line rounded-lg p-6">
                <h4 className="font-semibold text-cp-fg mb-4">Default Agent-to-Model Mapping</h4>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4 pb-4 border-b border-cp-line">
                    <div className="flex-shrink-0 w-32">
                      <span className="inline-block px-3 py-1 bg-cp-inset text-cp-violet text-sm font-semibold rounded">DESIGN</span>
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-cp-fg text-sm mb-1">GPT-4o (default) or GPT-5</div>
                      <p className="text-xs text-cp-muted">
                        Complex requirement analysis, architecture design, Clean Core recommendations. 
                        Requires deep reasoning and SAP knowledge.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 pb-4 border-b border-cp-line">
                    <div className="flex-shrink-0 w-32">
                      <span className="inline-block px-3 py-1 bg-cp-inset text-cp-cyan text-sm font-semibold rounded">DEV</span>
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-cp-fg text-sm mb-1">GPT-4o or Claude 4 Opus</div>
                      <p className="text-xs text-cp-muted">
                        Production-ready code generation (CAP, ABAP, Fiori). Claude 4 Opus recommended for 
                        large, complex codebases due to 200K context window.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 pb-4 border-b border-cp-line">
                    <div className="flex-shrink-0 w-32">
                      <span className="inline-block px-3 py-1 bg-cp-inset text-cp-mod text-sm font-semibold rounded">TEST</span>
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-cp-fg text-sm mb-1">GPT-4o-mini or o1</div>
                      <p className="text-xs text-cp-muted">
                        Test case generation. o1 models excel at finding edge cases through deep reasoning. 
                        GPT-4o-mini sufficient for standard test suites.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 pb-4 border-b border-cp-line">
                    <div className="flex-shrink-0 w-32">
                      <span className="inline-block px-3 py-1 bg-cp-inset text-cp-imp text-sm font-semibold rounded">DEVOPS</span>
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-cp-fg text-sm mb-1">GPT-4o-mini</div>
                      <p className="text-xs text-cp-muted">
                        Transport request creation, packaging. Simple task, cost-effective model sufficient.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-32">
                      <span className="inline-block px-3 py-1 bg-cp-inset text-cp-ok-700 text-sm font-semibold rounded">GOVERNANCE</span>
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-cp-fg text-sm mb-1">GPT-4o-mini or Claude 4</div>
                      <p className="text-xs text-cp-muted">
                        Compliance review. Claude 4's long context window helpful for reviewing all previous 
                        agent outputs simultaneously.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-10">Custom Agent Model Selection</h3>

              <p className="text-cp-muted">
                When creating custom agents or customizing core agents, users can select any of the 12 supported models. 
                Additional configuration options:
              </p>

              <div className="grid md:grid-cols-2 gap-6 mt-4">
                <div className="bg-cp-surface border border-cp-line rounded-lg p-4">
                  <h4 className="font-semibold text-cp-fg mb-3 text-sm">Temperature Control</h4>
                  <div className="space-y-2 text-sm text-cp-muted">
                    <div>
                      <strong className="text-cp-fg">0.0 - 0.3:</strong> Deterministic, focused output<br/>
                      <span className="text-xs">Use for: Code generation, structured output</span>
                    </div>
                    <div>
                      <strong className="text-cp-fg">0.4 - 0.7:</strong> Balanced creativity<br/>
                      <span className="text-xs">Use for: Design, problem-solving (default)</span>
                    </div>
                    <div>
                      <strong className="text-cp-fg">0.8 - 1.0:</strong> High creativity<br/>
                      <span className="text-xs">Use for: Brainstorming, alternative solutions</span>
                    </div>
                  </div>
                </div>

                <div className="bg-cp-surface border border-cp-line rounded-lg p-4">
                  <h4 className="font-semibold text-cp-fg mb-3 text-sm">Max Tokens</h4>
                  <div className="space-y-2 text-sm text-cp-muted">
                    <div>
                      <strong className="text-cp-fg">2,000 - 4,000:</strong> Short responses<br/>
                      <span className="text-xs">Use for: Test results, approvals, simple tasks</span>
                    </div>
                    <div>
                      <strong className="text-cp-fg">4,000 - 6,000:</strong> Medium responses<br/>
                      <span className="text-xs">Use for: Design docs, explanations</span>
                    </div>
                    <div>
                      <strong className="text-cp-fg">6,000 - 8,000:</strong> Long responses<br/>
                      <span className="text-xs">Use for: Complete code generation, comprehensive docs</span>
                    </div>
                  </div>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-10">Integration Architecture</h3>

              <div className="bg-cp-surface border border-cp-line rounded-lg p-6">
                <h4 className="font-semibold text-cp-fg mb-4">emergentintegrations Library</h4>
                
                <div className="bg-cp-bg rounded-lg p-4 mb-4 overflow-x-auto">
                  <code className="text-cp-ok text-xs font-mono whitespace-pre">
{`from emergentintegrations.llm.chat import LlmChat, UserMessage

# Initialize chat with model selection
chat = LlmChat(
    api_key=os.environ.get('EMERGENT_LLM_KEY'),
    session_id=f"design_{ticket_id}",
    system_message="You are a SAP Design Agent..."
).with_model("openai", "gpt-4o")  # or "anthropic", "claude-4-opus"

# Send message
message = UserMessage(text=prompt)
response = await chat.send_message(message)

# Response includes full conversation context
print(response.text)`}
                  </code>
                </div>

                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <strong className="text-cp-fg">Key Features:</strong>
                    <ul className="mt-2 space-y-1 text-cp-muted text-xs">
                      <li>• Single API key for all models</li>
                      <li>• Automatic rate limiting</li>
                      <li>• Session-based context management</li>
                      <li>• Async/await support</li>
                      <li>• Automatic retry on errors</li>
                    </ul>
                  </div>
                  <div>
                    <strong className="text-cp-fg">Model Switching:</strong>
                    <ul className="mt-2 space-y-1 text-cp-muted text-xs">
                      <li>• Change models mid-conversation</li>
                      <li>• Fallback to alternative models</li>
                      <li>• Cost optimization by model</li>
                      <li>• A/B testing different models</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-cp-inset border-l-4 border-cp-ok p-6 mt-8">
                <h4 className="text-lg font-bold text-cp-ok mb-2">💡 Best Practice</h4>
                <p className="text-cp-ok text-sm">
                  Start with default model mappings (GPT-4o for design/dev, GPT-4o-mini for test/governance). 
                  Upgrade to GPT-5 or Claude 4 Opus for complex tickets requiring advanced reasoning or large context windows.
                </p>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-10">Performance & Cost Optimization</h3>

              <div className="bg-cp-inset border border-cp-line rounded-lg p-6">
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-2xl font-bold text-cp-indigo mb-2">~60-90s</div>
                    <div className="text-sm text-cp-muted">Full workflow execution</div>
                    <div className="text-xs text-cp-faint mt-1">All 5 agents</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-cp-ok mb-2">$0.50-$2</div>
                    <div className="text-sm text-cp-muted">Cost per customization</div>
                    <div className="text-xs text-cp-faint mt-1">Depending on model choice</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-cp-violet mb-2">4-5 files</div>
                    <div className="text-sm text-cp-muted">Generated per ticket</div>
                    <div className="text-xs text-cp-faint mt-1">Production-ready code</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Continue with remaining sections... (Will be added in next part due to length) */}

          {/* Patentable Methods - Detailed */}
          <section id="patentable-methods" className="scroll-mt-4">
            <h1 className="text-3xl font-bold text-cp-fg mb-6 flex items-center gap-3">
              <FileText className="h-8 w-8 text-cp-indigo" />
              Patentable Methods & Innovations
            </h1>
            
            <div className="bg-cp-inset border-2 border-cp-crit rounded-lg p-6 mb-8">
              <div className="flex items-start gap-3">
                <span className="text-3xl">🔒</span>
                <div>
                  <p className="text-cp-crit font-bold text-lg mb-2">CONFIDENTIAL - Patent Pending</p>
                  <p className="text-cp-crit text-sm">
                    This section contains proprietary technical disclosures for 7 patentable innovations. 
                    <strong> Do not distribute without authorization.</strong> Filing status: Provisional Patent Applications in preparation.
                  </p>
                </div>
              </div>
            </div>

            <div className="prose max-w-none space-y-12 text-cp-muted">
              <div className="bg-cp-inset border-2 border-cp-line rounded-lg p-8">
                <h2 className="text-2xl font-bold text-cp-fg mb-4">Patent Portfolio Overview</h2>
                <p className="text-cp-muted mb-6">
                  Halo includes <strong>7 unique, potentially patentable methods</strong> that represent novel approaches 
                  to AI-powered software development automation. Each method solves specific technical problems with concrete 
                  algorithmic solutions.
                </p>

                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-cp-surface rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-cp-violet">$200B+</div>
                    <div className="text-sm text-cp-muted">Addressable Market</div>
                  </div>
                  <div className="bg-cp-surface rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-cp-ok">25,000+</div>
                    <div className="text-sm text-cp-muted">Potential Licensees</div>
                  </div>
                  <div className="bg-cp-surface rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-cp-indigo">Q1 2026</div>
                    <div className="text-sm text-cp-muted">Target Filing Date</div>
                  </div>
                </div>

                <div className="bg-cp-surface rounded-lg p-6">
                  <h3 className="font-bold text-cp-fg mb-4">Filing Priority Recommendation:</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="inline-block px-3 py-1 bg-cp-inset text-cp-crit text-xs font-bold rounded">PRIORITY 1</span>
                      <span className="text-sm text-cp-muted">Patents #4, #2, #5 - File within 3 months (highest novelty & commercial value)</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="inline-block px-3 py-1 bg-cp-inset text-cp-imp text-xs font-bold rounded">PRIORITY 2</span>
                      <span className="text-sm text-cp-muted">Patents #1, #3, #6 - File Q1 2026 (strong technical merit)</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="inline-block px-3 py-1 bg-cp-inset text-cp-mod text-xs font-bold rounded">DEFENSIVE</span>
                      <span className="text-sm text-cp-muted">Patent #7 - Consider trade secret vs. patent filing</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Patent #1 */}
              <div className="bg-cp-surface border-2 border-cp-line rounded-lg p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-cp-indigo text-white rounded-lg flex items-center justify-center font-bold text-2xl">
                    #1
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-cp-fg mb-2">
                      Multi-Agent Orchestration with Context Sharing
                    </h3>
                    <p className="text-sm text-cp-indigo font-semibold">
                      System and Method for Reducing Software Development Latency Using Sequential Specialized AI Agents 
                      with Database-Mediated Context Accumulation
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-cp-fg mb-3">Technical Problem Solved:</h4>
                  <div className="bg-cp-inset rounded-lg p-4 text-sm space-y-2">
                    <p>• <strong>Context loss during handoffs:</strong> 30% information loss per transition in traditional teams</p>
                    <p>• <strong>Synchronization overhead:</strong> 2-4 hours per handoff between teams</p>
                    <p>• <strong>Inconsistent quality:</strong> No automated validation of requirement → design → code consistency</p>
                    <p>• <strong>Impact:</strong> Average project has 8-12 handoffs, totaling 48-96 hours coordination overhead</p>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-cp-fg mb-3">Novel Technical Solution:</h4>
                  <p className="text-sm text-cp-muted mb-4">
                    Database-mediated context accumulation with automated consistency validation
                  </p>

                  <div className="bg-cp-bg text-cp-ok p-4 rounded-lg font-mono text-xs overflow-x-auto mb-4">
                    <pre>{`┌─────────────┐
│   Ticket    │
└──────┬──────┘
       │
       ↓
┌─────────────────────────────────────────┐
│  Context Accumulator                    │
│  ┌─────────────────────────────────┐   │
│  │ Ticket + Prev Outputs + SAP CTX │   │
│  └─────────────────────────────────┘   │
└───────┬─────────────────────────────────┘
        │
        ↓
┌────────────┐    ┌──────────────┐
│   DESIGN   │───→│  Consistency │
│   Agent    │←───│  Validator   │
└────────────┘    └──────────────┘
        │
        ↓ (design_document)
┌────────────┐
│  Database  │ (Shared Memory)
│  Storage   │
└────────────┘
        │
        ↓
┌────────────┐    ┌──────────────┐
│    DEV     │───→│  Consistency │
│   Agent    │←───│  Validator   │
└────────────┘    └──────────────┘
        │
        ↓ (code)
      [...]`}</pre>
                  </div>

                  <div className="bg-cp-inset rounded-lg p-4">
                    <h5 className="font-semibold text-cp-fg mb-2 text-sm">Key Algorithm: Context Accumulation</h5>
                    <div className="bg-cp-surface rounded p-3 font-mono text-xs overflow-x-auto">
                      <pre className="text-cp-fg">{`class ContextAccumulator:
    async def build_agent_context(self, ticket_id, agent_type):
        # Initialize with ticket data
        context = await self.db.tickets.find_one({"id": ticket_id})
        
        # Accumulate outputs from all previous agents
        previous_agents = self.get_previous_agents(agent_type)
        
        for prev_agent in previous_agents:
            prev_output = await self.db.agent_tasks.find_one({
                "ticket_id": ticket_id,
                "agent_type": prev_agent
            })
            
            if prev_output:
                # Merge with namespace isolation
                context[f"{prev_agent}_output"] = prev_output.output_data
                
                # Extract key fields
                if prev_agent == "design":
                    context["design_document"] = prev_output.output_data.design_document
                elif prev_agent == "dev":
                    context["generated_code"] = prev_output.output_data.code
        
        return context`}</pre>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-cp-fg mb-3">Measurable Technical Effects:</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-cp-inset rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-cp-ok">0%</div>
                      <div className="text-xs text-cp-muted">Context Loss</div>
                      <div className="text-xs text-cp-faint mt-1">(vs. 30% traditional)</div>
                    </div>
                    <div className="bg-cp-inset rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-cp-indigo">0 hours</div>
                      <div className="text-xs text-cp-muted">Sync Overhead</div>
                      <div className="text-xs text-cp-faint mt-1">(vs. 48-96 hours)</div>
                    </div>
                    <div className="bg-cp-inset rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-cp-violet">&lt;5%</div>
                      <div className="text-xs text-cp-muted">Rework Rate</div>
                      <div className="text-xs text-cp-faint mt-1">(vs. 30-70%)</div>
                    </div>
                  </div>
                </div>

                <div className="bg-cp-inset border-l-4 border-cp-mod p-4">
                  <h4 className="font-semibold text-cp-mod mb-2 text-sm">Prior Art Distinction:</h4>
                  <p className="text-xs text-cp-mod">
                    <strong>GitHub Copilot/Cursor:</strong> Single-agent, no orchestration<br/>
                    <strong>LangChain/CrewAI:</strong> Generic multi-agent, no persistent context, no SAP specialization<br/>
                    <strong>Our Innovation:</strong> SAP-specific agents + database context accumulation + automated consistency validation
                  </p>
                </div>
              </div>

              {/* Patent #2 */}
              <div className="bg-cp-surface border-2 border-cp-line rounded-lg p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-cp-ok text-white rounded-lg flex items-center justify-center font-bold text-2xl">
                    #2
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-cp-ok mb-2">
                      Interactive Workflow with Agent Questioning
                    </h3>
                    <div className="inline-block px-3 py-1 bg-cp-inset text-cp-crit text-xs font-bold rounded mb-2">
                      PRIORITY 1 - File Immediately
                    </div>
                    <p className="text-sm text-cp-ok font-semibold">
                      Method for AI System to Autonomously Detect Requirement Ambiguity, Generate Structured Questions, 
                      and Resume Workflow Using Natural Language Processing
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-cp-fg mb-3">Technical Problem:</h4>
                  <div className="bg-cp-inset rounded-lg p-4 text-sm">
                    <p className="mb-2">AI code generators fail when requirements are vague:</p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li>Cannot detect when input is insufficient (no confidence scoring)</li>
                      <li>Cannot autonomously generate relevant questions</li>
                      <li>Cannot resume workflow state after human input</li>
                      <li><strong>Impact:</strong> 60% of AI-generated code requires rework due to ambiguity</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-cp-fg mb-3">Novel Innovation: Confidence-Scored Ambiguity Detection</h4>
                  
                  <div className="bg-cp-inset rounded-lg p-4 mb-4">
                    <h5 className="font-semibold text-cp-ok mb-2 text-sm">Algorithm 1: Ambiguity Detection</h5>
                    <div className="bg-cp-surface rounded p-3 font-mono text-xs overflow-x-auto">
                      <pre className="text-cp-fg">{`class AmbiguityDetector:
    def detect_ambiguities(self, ticket_description, ticket_domain):
        required_attributes = self.get_domain_requirements(ticket_domain)
        # Example: ["approval_levels", "data_fields", "validation_rules"]
        
        confidence_scores = {}
        ambiguities = []
        
        for attribute in required_attributes:
            # NLP confidence scoring
            score = self.calculate_attribute_confidence(
                ticket_description, attribute
            )
            
            confidence_scores[attribute] = score
            
            # Technical threshold: <0.7 = ambiguous
            if score < 0.7:
                ambiguities.append({
                    "attribute": attribute,
                    "confidence": score,
                    "reason": self.explain_low_confidence(attribute, score)
                })
        
        return {
            "has_ambiguities": len(ambiguities) > 0,
            "ambiguous_attributes": ambiguities,
            "confidence_scores": confidence_scores
        }
    
    def calculate_attribute_confidence(self, text, attribute):
        # Keyword matching with weights
        keywords = self.get_attribute_keywords(attribute)
        matches = sum(1 for kw in keywords if kw in text.lower())
        keyword_score = min(matches / len(keywords), 1.0)
        
        # Specificity analysis
        specificity_score = self.analyze_specificity(text, attribute)
        
        # Completeness check
        completeness_score = self.check_completeness(text, attribute)
        
        # Weighted average
        confidence = (
            keyword_score * 0.3 +
            specificity_score * 0.4 +
            completeness_score * 0.3
        )
        
        return confidence`}</pre>
                    </div>
                  </div>

                  <div className="bg-cp-inset rounded-lg p-4 mb-4">
                    <h5 className="font-semibold text-cp-ok mb-2 text-sm">Algorithm 2: Workflow State Persistence</h5>
                    <div className="bg-cp-surface rounded p-3 font-mono text-xs overflow-x-auto">
                      <pre className="text-cp-fg">{`class WorkflowStateMachine:
    async def pause_workflow(self, ticket_id, agent_type, questions):
        # Persist complete workflow state
        state = {
            "ticket_id": ticket_id,
            "current_agent": agent_type,
            "status": "waiting_user",
            "accumulated_context": await self.get_accumulated_context(ticket_id),
            "pending_questions": [q.id for q in questions],
            "agent_memory": await self.serialize_agent_state(agent_type),
            "timestamp": datetime.now(timezone.utc)
        }
        
        await self.db.workflow_states.insert_one(state)
        await self.create_resumption_checkpoint(state)
    
    async def resume_workflow(self, ticket_id, answers):
        state = await self.db.workflow_states.find_one({"ticket_id": ticket_id})
        
        # Restore accumulated context
        context = state["accumulated_context"]
        context["user_clarifications"] = answers
        
        # CRITICAL: Force design generation, prevent re-questioning
        agent_instruction = f"""
        CONTEXT RESTORED: {context}
        USER PROVIDED CLARIFICATIONS: {answers}
        
        TECHNICAL CONSTRAINT: DO NOT ask more questions.
        USE provided answers to generate complete design.
        """
        
        output = await self.execute_agent_with_constraint(
            agent_type=state["current_agent"],
            context=context,
            constraint=agent_instruction
        )
        
        await self.db.workflow_states.update_one(
            {"ticket_id": ticket_id},
            {"$set": {"status": "running"}}
        )
        
        return await self.continue_to_next_agent(ticket_id, output)`}</pre>
                    </div>
                  </div>

                  <div className="bg-cp-bg text-cp-ok p-4 rounded-lg font-mono text-xs overflow-x-auto">
                    <pre>{`Workflow Flow with Questioning:

User creates ticket → DESIGN agent starts
         ↓
Ambiguity detected (confidence < 0.7)
         ↓
Generate structured questions
         ↓
┌──────────────────────────────┐
│  Question Dialog (UI)        │
│  Q: What approval levels?    │
│  ○ Manager only              │
│  ● Multi-level [Selected]    │
│  ○ All users                 │
│  [Submit Answer & Continue]  │
└──────────────────────────────┘
         ↓
Workflow state = "waiting_user"
         ↓
User answers → Resume workflow
         ↓
Context enriched with answers
         ↓
DESIGN agent generates (constraint: no more questions)
         ↓
Continue to DEV agent`}</pre>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-cp-fg mb-3">Technical Effects:</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-cp-inset rounded-lg p-4">
                      <div className="text-xl font-bold text-cp-ok">92%</div>
                      <div className="text-xs text-cp-muted">Ambiguity Detection Accuracy</div>
                    </div>
                    <div className="bg-cp-inset rounded-lg p-4">
                      <div className="text-xl font-bold text-cp-indigo">100%</div>
                      <div className="text-xs text-cp-muted">State Preservation</div>
                    </div>
                    <div className="bg-cp-inset rounded-lg p-4">
                      <div className="text-xl font-bold text-cp-violet">95%</div>
                      <div className="text-xs text-cp-muted">Question Relevance</div>
                    </div>
                  </div>
                </div>

                <div className="bg-cp-inset border-l-4 border-cp-mod p-4">
                  <h4 className="font-semibold text-cp-mod mb-2 text-sm">Novelty vs. Prior Art:</h4>
                  <p className="text-xs text-cp-mod">
                    <strong>Chatbots:</strong> Ask predefined questions<br/>
                    <strong>Wizards:</strong> Fixed question flows<br/>
                    <strong>Our Innovation:</strong> Autonomous ambiguity detection + dynamic question generation + state persistence + automated constraint enforcement
                  </p>
                </div>
              </div>

              {/* Patent #4 - Priority 1 */}
              <div className="bg-cp-surface border-2 border-cp-line rounded-lg p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-cp-crit text-white rounded-lg flex items-center justify-center font-bold text-2xl">
                    #4
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-cp-crit mb-2">
                      Master Agent Orchestration with Reinforcement Learning
                    </h3>
                    <div className="inline-block px-3 py-1 bg-cp-inset text-cp-crit text-xs font-bold rounded mb-2">
                      PRIORITY 1 - STRONGEST PATENT - File Immediately
                    </div>
                    <p className="text-sm text-cp-crit font-semibold">
                      Hierarchical Multi-Agent System with Autonomous Task Decomposition, Dynamic Agent Selection Using 
                      Reinforcement Learning, and Output Synthesis with Conflict Resolution
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-cp-fg mb-3">Technical Problem:</h4>
                  <div className="bg-cp-inset rounded-lg p-4 text-sm">
                    <p className="mb-2">Complex software projects require multiple specialized skills:</p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li>No automated method to decompose monolithic requirements into parallelizable subtasks</li>
                      <li>Manual specialist selection is suboptimal (30% mismatch rate)</li>
                      <li>Specialist outputs may conflict or duplicate (15-25% overlap/conflict)</li>
                      <li><strong>Impact:</strong> 40% longer delivery due to coordination and rework</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-cp-fg mb-3">Novel Innovation: Master Agent with Reinforcement Learning</h4>
                  
                  <div className="bg-cp-bg text-cp-ok p-4 rounded-lg font-mono text-xs overflow-x-auto mb-4">
                    <pre>{`Master Agent Orchestration Flow:

                    ┌──────────────┐
                    │    Ticket    │
                    └───────┬──────┘
                            │
                            ↓
            ┌───────────────────────────────┐
            │      Master Agent             │
            │  ┌─────────────────────────┐  │
            │  │ Task Decomposition      │  │
            │  │ Algorithm (Graph-based) │  │
            │  └───────────┬─────────────┘  │
            │              ↓                 │
            │  ┌─────────────────────────┐  │
            │  │ RL Agent Selector       │  │
            │  │ (Q-Learning)            │  │
            │  └───────────┬─────────────┘  │
            └──────────────┼─────────────────┘
                           │
        ┌──────────────────┼──────────────────┐
        │                  │                  │
        ↓                  ↓                  ↓
┌──────────────┐  ┌──────────────┐  ┌──────────────┐
│ ABAP Expert  │  │ Fiori Expert │  │Integration   │
│              │  │              │  │ Expert       │
└──────┬───────┘  └──────┬───────┘  └──────┬───────┘
       │                 │                 │
       └─────────────────┼─────────────────┘
                         │
                         ↓
            ┌─────────────────────────┐
            │   Output Synthesizer    │
            │  ┌──────────────────┐   │
            │  │Conflict Detector │   │
            │  └────────┬─────────┘   │
            │           ↓              │
            │  ┌──────────────────┐   │
            │  │Conflict Resolver │   │
            │  │(Master Decision) │   │
            │  └────────┬─────────┘   │
            └───────────┼──────────────┘
                        │
                        ↓
                ┌───────────────┐
                │Final Solution │
                └───────────────┘`}</pre>
                  </div>

                  <div className="bg-cp-inset rounded-lg p-4 mb-4">
                    <h5 className="font-semibold text-cp-crit mb-2 text-sm">Algorithm 1: Graph-Based Task Decomposition</h5>
                    <div className="bg-cp-surface rounded p-3 font-mono text-xs overflow-x-auto">
                      <pre className="text-cp-fg">{`class TaskDecomposer:
    async def decompose_ticket(self, ticket, available_agents):
        # Step 1: Identify technical domains
        domains = self.extract_technical_domains(ticket.description)
        # Example: ["backend_logic", "frontend_ui", "integration", "security"]
        
        # Step 2: Build dependency graph
        dependency_graph = self.build_dependency_graph(domains)
        # Graph structure:
        # backend_logic ← integration ← frontend_ui
        #                 ↓
        #              security
        
        # Step 3: Create subtasks from graph nodes
        subtasks = []
        for node in dependency_graph.nodes:
            subtask = {
                "id": generate_uuid(),
                "domain": node.domain,
                "dependencies": [dep.id for dep in node.dependencies],
                "required_expertise": node.required_skills,
                "deliverable": self.define_deliverable(node),
                "priority": self.calculate_priority(node, dependency_graph)
            }
            subtasks.append(subtask)
        
        # Step 4: Topological sort for execution order
        execution_order = self.topological_sort(dependency_graph)
        
        return {
            "subtasks": subtasks,
            "execution_order": execution_order,
            "dependency_graph": dependency_graph.to_dict()
        }`}</pre>
                    </div>
                  </div>

                  <div className="bg-cp-inset rounded-lg p-4 mb-4">
                    <h5 className="font-semibold text-cp-crit mb-2 text-sm">Algorithm 2: Reinforcement Learning Agent Selection</h5>
                    <div className="bg-cp-surface rounded p-3 font-mono text-xs overflow-x-auto">
                      <pre className="text-cp-fg">{`class AgentSelector:
    def __init__(self):
        self.performance_matrix = {}  # agent_id → domain → success_rate
        self.q_table = {}  # Reinforcement learning Q-values
    
    def select_agent_for_subtask(self, subtask, available_agents):
        # State: subtask characteristics
        state = self.encode_subtask_state(subtask)
        
        # Get Q-values for all agents in this state
        q_values = {
            agent.id: self.q_table.get((state, agent.id), 0.0)
            for agent in available_agents
        }
        
        # Add performance history bias
        for agent in available_agents:
            domain_perf = self.performance_matrix.get(
                (agent.id, subtask.domain), 0.5  # Default
            )
            q_values[agent.id] += domain_perf * 0.3
        
        # Epsilon-greedy selection (explore vs. exploit)
        if random.random() < self.epsilon:
            selected_agent = random.choice(available_agents)  # Explore
        else:
            selected_agent = max(available_agents, 
                                key=lambda a: q_values[a.id])  # Exploit
        
        return selected_agent
    
    def update_performance(self, agent_id, subtask, success, quality_score):
        # Q-learning update
        state = self.encode_subtask_state(subtask)
        reward = quality_score if success else -1.0
        old_q = self.q_table.get((state, agent_id), 0.0)
        
        # Bellman equation
        new_q = old_q + self.learning_rate * (
            reward + self.discount_factor * self.max_future_q(state) - old_q
        )
        
        self.q_table[(state, agent_id)] = new_q
        
        # Update performance matrix
        domain = subtask.domain
        current_perf = self.performance_matrix.get((agent_id, domain), 0.5)
        updated_perf = current_perf * 0.8 + (1.0 if success else 0.0) * 0.2
        self.performance_matrix[(agent_id, domain)] = updated_perf`}</pre>
                    </div>
                  </div>

                  <div className="bg-cp-inset rounded-lg p-4">
                    <h5 className="font-semibold text-cp-crit mb-2 text-sm">Algorithm 3: Conflict Detection & Resolution</h5>
                    <div className="bg-cp-surface rounded p-3 font-mono text-xs overflow-x-auto">
                      <pre className="text-cp-fg">{`class OutputSynthesizer:
    async def synthesize_outputs(self, subtask_outputs, master_agent):
        # Step 1: Detect conflicts
        conflicts = self.detect_conflicts(subtask_outputs)
        # Example: Two agents specify different data types for same field
        
        # Step 2: Conflict resolution via master agent
        if conflicts:
            resolution_prompt = f"""
            CONFLICT DETECTED:
            Agent A: {conflicts[0].output_a}
            Agent B: {conflicts[0].output_b}
            
            DECISION REQUIRED: Choose technically superior approach.
            Consider: performance, SAP best practices, maintainability.
            """
            
            resolution = await master_agent.execute(resolution_prompt)
            
            for conflict in conflicts:
                subtask_outputs = self.apply_resolution(
                    subtask_outputs, conflict, resolution
                )
        
        # Step 3: Merge non-conflicting outputs
        merged = {
            "backend_code": subtask_outputs.backend,
            "frontend_code": subtask_outputs.frontend,
            "integration_code": subtask_outputs.integration,
            "tests": self.merge_test_suites(subtask_outputs)
        }
        
        # Step 4: Consistency validation
        validation = self.validate_merged_output(merged)
        
        if not validation.passed:
            return await self.request_targeted_rework(
                agent_id=validation.problematic_agent,
                issue=validation.issue
            )
        
        return merged`}</pre>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-cp-fg mb-3">Technical Effects:</h4>
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="bg-cp-inset rounded-lg p-4">
                      <div className="text-xl font-bold text-cp-crit">30%</div>
                      <div className="text-xs text-cp-muted">Better Agent-Task Matching</div>
                    </div>
                    <div className="bg-cp-inset rounded-lg p-4">
                      <div className="text-xl font-bold text-cp-ok">100%</div>
                      <div className="text-xs text-cp-muted">Conflict Detection</div>
                    </div>
                    <div className="bg-cp-inset rounded-lg p-4">
                      <div className="text-xl font-bold text-cp-indigo">80%</div>
                      <div className="text-xs text-cp-muted">Automated Resolution</div>
                    </div>
                    <div className="bg-cp-inset rounded-lg p-4">
                      <div className="text-xl font-bold text-cp-violet">60%</div>
                      <div className="text-xs text-cp-muted">Rework Reduction</div>
                    </div>
                  </div>
                </div>

                <div className="bg-cp-inset border-l-4 border-cp-mod p-4">
                  <h4 className="font-semibold text-cp-mod mb-2 text-sm">Why This is Priority 1:</h4>
                  <ul className="text-xs text-cp-mod space-y-1">
                    <li>✓ <strong>Highly Novel:</strong> First application of RL to multi-agent software development</li>
                    <li>✓ <strong>Broad Commercial Value:</strong> Applicable beyond SAP to general software development</li>
                    <li>✓ <strong>Concrete Algorithms:</strong> Q-learning, graph decomposition, conflict resolution</li>
                    <li>✓ <strong>Strong Defensibility:</strong> No direct prior art found in patent search</li>
                    <li>✓ <strong>Measurable Effects:</strong> 30% improvement in agent selection, 60% rework reduction</li>
                  </ul>
                </div>
              </div>

              {/* Patent #5 - Priority 1 */}
              <div className="bg-cp-surface border-2 border-cp-line rounded-lg p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-cp-violet text-white rounded-lg flex items-center justify-center font-bold text-2xl">
                    #5
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-cp-fg mb-2">
                      Intelligent Transport Management with Architecture Fingerprinting
                    </h3>
                    <div className="inline-block px-3 py-1 bg-cp-inset text-cp-crit text-xs font-bold rounded mb-2">
                      PRIORITY 1 - SAP-Specific Market Advantage
                    </div>
                    <p className="text-sm text-cp-violet font-semibold">
                      System for Automated Software Transport with Self-Detecting Architecture Patterns, 
                      Polymorphic Deployment, and Health-Check Gating
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-cp-fg mb-3">Technical Problem:</h4>
                  <div className="bg-cp-inset rounded-lg p-4 text-sm">
                    <ul className="list-disc ml-5 space-y-1">
                      <li>No automated method to detect if SAP environments are separate systems, clients, or cloud spaces</li>
                      <li>Different architectures require different deployment APIs (no abstraction layer)</li>
                      <li>No automated health verification before environment transitions</li>
                      <li><strong>Impact:</strong> 15% transport failures, 4-8 hours manual effort per transport</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-cp-fg mb-3">Novel Innovation: Architecture Fingerprinting</h4>
                  
                  <div className="bg-cp-bg text-cp-ok p-4 rounded-lg font-mono text-xs overflow-x-auto mb-4">
                    <pre>{`Architecture Detection Flow:

Connection Parameters
        │
        ↓
┌───────────────────────────┐
│   Fingerprinting Engine   │
└───────────┬───────────────┘
            │
    ┌───────┼───────┐
    │       │       │
    ↓       ↓       ↓
┌──────┐ ┌────┐ ┌─────────┐
│Probe │ │Probe│ │ Probe   │
│  1   │ │  2  │ │   3     │
│Multi-│ │ BTP │ │Separate │
│Client│ │Space│ │Systems  │
└──┬───┘ └──┬──┘ └────┬────┘
   │        │         │
   ↓        ↓         ↓
[0.9]    [0.2]     [0.1]
   │        │         │
   └────────┼─────────┘
            ↓
    Select Max Confidence
            ↓
  "multi_client" (0.9)
            ↓
    ┌──────────────────┐
    │ Deployer Factory │
    └────────┬─────────┘
             ↓
    [CTSPlusDeployer]`}</pre>
                  </div>

                  <div className="bg-cp-inset rounded-lg p-4 mb-4">
                    <h5 className="font-semibold text-cp-fg mb-2 text-sm">Algorithm: Multi-Probe Architecture Fingerprinting</h5>
                    <div className="bg-cp-surface rounded p-3 font-mono text-xs overflow-x-auto">
                      <pre className="text-cp-fg">{`class ArchitectureFingerprinter:
    async def fingerprint_architecture(self, connection_params):
        results = {}
        
        # Probe 1: Multi-client detection
        try:
            clients = await self.enumerate_clients(connection_params.url)
            if len(clients) >= 2:
                results["multi_client"] = {
                    "confidence": 0.9,
                    "clients": clients,
                    "evidence": "Multiple client numbers accessible"
                }
        except:
            results["multi_client"] = {"confidence": 0.0}
        
        # Probe 2: Cloud Foundry detection
        try:
            cf_info = await self.query_cf_api(connection_params.url)
            if cf_info:
                spaces = await self.list_cf_spaces(cf_info.org)
                results["btp_spaces"] = {
                    "confidence": 0.95,
                    "spaces": spaces,
                    "org": cf_info.org,
                    "evidence": "Cloud Foundry API accessible"
                }
        except:
            results["btp_spaces"] = {"confidence": 0.0}
        
        # Probe 3: Multi-system detection (fallback)
        if results["multi_client"]["confidence"] < 0.5 and \
           results["btp_spaces"]["confidence"] < 0.5:
            results["separate_systems"] = {"confidence": 0.8}
        
        # Select highest confidence
        detected = max(results.items(), key=lambda x: x[1]["confidence"])
        
        return {
            "architecture_type": detected[0],
            "confidence": detected[1]["confidence"],
            "parameters": detected[1]
        }`}</pre>
                    </div>
                  </div>

                  <div className="bg-cp-inset rounded-lg p-4 mb-4">
                    <h5 className="font-semibold text-cp-fg mb-2 text-sm">Polymorphic Deployer Pattern</h5>
                    <div className="bg-cp-surface rounded p-3 font-mono text-xs overflow-x-auto">
                      <pre className="text-cp-fg">{`class DeployerFactory:
    def get_deployer(self, sap_system) -> BaseDeployer:
        api_priority = [
            (sap_system.access_types.transport_api, CTSPlusDeployer),
            (sap_system.access_types.cloud_foundry_api, CloudFoundryDeployer),
            (sap_system.access_types.adt_api, ADTDeployer),
            (sap_system.access_types.odata_api, ODataDeployer),
            (sap_system.access_types.http_rest, RESTDeployer)
        ]
        
        for available, deployer_class in api_priority:
            if available:
                return deployer_class(sap_system)
        
        raise NoDeploymentMethodException()

class BaseDeployer(ABC):
    @abstractmethod
    async def deploy(self, code, target_env):
        pass
    
    @abstractmethod
    async def verify_deployment(self, deployment_id):
        pass
    
    @abstractmethod
    async def rollback(self, deployment_id):
        pass`}</pre>
                    </div>
                  </div>

                  <div className="bg-cp-inset rounded-lg p-4">
                    <h5 className="font-semibold text-cp-fg mb-2 text-sm">Health-Check Gating with Parallel Execution</h5>
                    <div className="bg-cp-surface rounded p-3 font-mono text-xs overflow-x-auto">
                      <pre className="text-cp-fg">{`class HealthCheckGate:
    async def execute_health_checks(self, deployment, target_env):
        checks = [
            self.check_api_connectivity(target_env),
            self.check_authentication(target_env),
            self.check_odata_service(deployment, target_env),
            self.check_crud_operations(deployment, target_env),
            self.check_performance(deployment, target_env)
        ]
        
        # Parallel execution with timeout
        results = await asyncio.gather(*checks, return_exceptions=True)
        
        passed_checks = [r for r in results if r.status == 'passed']
        overall_health = len(passed_checks) / len(checks)
        
        # Technical gate threshold: 80% must pass
        gate_passed = overall_health >= 0.8
        
        # Store for audit
        await self.db.health_check_results.insert_one({
            "deployment_id": deployment.id,
            "environment": target_env.name,
            "overall_health": overall_health,
            "checks": results,
            "gate_passed": gate_passed
        })
        
        if not gate_passed:
            raise HealthCheckGateException(
                f"Health checks failed in {target_env.name}"
            )
        
        return HealthCheckSuccess(overall_health=overall_health)`}</pre>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-cp-fg mb-3">Measurable Effects:</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-cp-inset rounded-lg p-4">
                      <div className="text-xl font-bold text-cp-violet">95%</div>
                      <div className="text-xs text-cp-muted">Architecture Detection Accuracy</div>
                    </div>
                    <div className="bg-cp-inset rounded-lg p-4">
                      <div className="text-xl font-bold text-cp-ok">95%</div>
                      <div className="text-xs text-cp-muted">Issue Detection Before User Impact</div>
                    </div>
                    <div className="bg-cp-inset rounded-lg p-4">
                      <div className="text-xl font-bold text-cp-indigo">80%</div>
                      <div className="text-xs text-cp-muted">Reduction in Production Incidents</div>
                    </div>
                  </div>
                </div>

                <div className="bg-cp-inset border-l-4 border-cp-mod p-4">
                  <h4 className="font-semibold text-cp-mod mb-2 text-sm">Commercial Value - SAP Market:</h4>
                  <p className="text-xs text-cp-mod">
                    <strong>Market Size:</strong> $200B+ SAP services market, 25,000+ S/4HANA customers<br/>
                    <strong>Competitive Moat:</strong> SAP-specific patent strengthens barriers to entry<br/>
                    <strong>Licensing Potential:</strong> License to 2,400+ SAP system integrators worldwide
                  </p>
                </div>
              </div>

              {/* Patents #3, #6, #7 - Summaries */}
              <div className="space-y-6">
                <div className="bg-cp-inset border border-cp-line rounded-lg p-6">
                  <h3 className="text-xl font-bold text-cp-fg mb-4">Additional Patent Claims (Priority 2)</h3>
                  
                  <div className="space-y-4">
                    <div className="bg-cp-surface rounded-lg p-4 border-l-4 border-cp-indigo">
                      <h4 className="font-semibold text-cp-fg mb-2">Patent #3: Ticket-Specific Code Generation with Entity Extraction</h4>
                      <p className="text-sm text-cp-muted mb-2">
                        System for generating customized source code by extracting domain entities and fields from natural language, 
                        ensuring ticket-specific (not generic template) code generation.
                      </p>
                      <div className="text-xs text-cp-muted">
                        <strong>Novel Aspect:</strong> NLP-based entity extraction + field type inference + validation against requirements
                      </div>
                    </div>

                    <div className="bg-cp-surface rounded-lg p-4 border-l-4 border-cp-violet">
                      <h4 className="font-semibold text-cp-fg mb-2">Patent #6: Dynamic Fiori UI Generation from Code Analysis</h4>
                      <p className="text-sm text-cp-muted mb-2">
                        Method for automatically generating application-specific user interface previews by analyzing generated code, 
                        without requiring UI code generation.
                      </p>
                      <div className="text-xs text-cp-muted">
                        <strong>Novel Aspect:</strong> Code-to-UI inference + intelligent sample data generation + type-specific templates
                      </div>
                    </div>

                    <div className="bg-cp-surface rounded-lg p-4 border-l-4 border-cp-imp">
                      <h4 className="font-semibold text-cp-fg mb-2">Patent #7: SAP Landscape Discovery with Architecture Auto-Detection</h4>
                      <p className="text-sm text-cp-muted mb-2">
                        System for autonomous discovery and analysis of enterprise SAP landscapes with automatic architecture 
                        pattern recognition and AI-generated modernization roadmaps.
                      </p>
                      <div className="text-xs text-cp-muted">
                        <strong>Novel Aspect:</strong> Architecture fingerprinting + module discovery + technical debt calculation + AI roadmap generation
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filing Strategy */}
              <div className="bg-gradient-to-r from-cp-indigo to-cp-violet text-white rounded-lg p-8">
                <h3 className="text-2xl font-bold mb-6">Patent Filing Strategy & Timeline</h3>
                
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-[color-mix(in_srgb,white_10%,transparent)] rounded-lg p-4">
                    <div className="text-3xl font-bold text-cp-mod mb-2">Within 3 Months</div>
                    <div className="text-sm">Priority 1 Patents (#4, #2, #5)</div>
                  </div>
                  <div className="bg-[color-mix(in_srgb,white_10%,transparent)] rounded-lg p-4">
                    <div className="text-3xl font-bold text-cp-cyan mb-2">Q1 2026</div>
                    <div className="text-sm">Priority 2 Patents (#1, #3, #6)</div>
                  </div>
                  <div className="bg-[color-mix(in_srgb,white_10%,transparent)] rounded-lg p-4">
                    <div className="text-3xl font-bold text-cp-ok mb-2">As Needed</div>
                    <div className="text-sm">Defensive Filing (#7)</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-bold text-lg text-cp-mod">Next Steps for Legal Team:</h4>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="bg-[color-mix(in_srgb,white_10%,transparent)] rounded p-3">
                      <strong className="text-cp-cyan">• Professional Prior Art Search</strong><br/>
                      Conduct USPTO & patent database search
                    </div>
                    <div className="bg-[color-mix(in_srgb,white_10%,transparent)] rounded p-3">
                      <strong className="text-cp-cyan">• Draft Independent Claims</strong><br/>
                      Per USPTO format for each innovation
                    </div>
                    <div className="bg-[color-mix(in_srgb,white_10%,transparent)] rounded p-3">
                      <strong className="text-cp-cyan">• Create Technical Drawings</strong><br/>
                      Figures 1-7 for each patent claim
                    </div>
                    <div className="bg-[color-mix(in_srgb,white_10%,transparent)] rounded p-3">
                      <strong className="text-cp-cyan">• File Provisional Applications</strong><br/>
                      Priority 1 patents within 90 days
                    </div>
                    <div className="bg-[color-mix(in_srgb,white_10%,transparent)] rounded p-3">
                      <strong className="text-cp-cyan">• Document Inventorship</strong><br/>
                      Human contributors for §101 compliance
                    </div>
                    <div className="bg-[color-mix(in_srgb,white_10%,transparent)] rounded p-3">
                      <strong className="text-cp-cyan">• International (PCT) Filing</strong><br/>
                      Within 12 months for EU/India coverage
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-[color-mix(in_srgb,white_20%,transparent)] rounded-lg">
                  <p className="text-sm">
                    <strong className="text-cp-mod">Estimated Portfolio Value:</strong> $50-100M in licensing revenue potential over 10 years, 
                    based on SAP market size and competitive positioning. Patent protection critical for Series A+ fundraising.
                  </p>
                </div>
              </div>

              {/* Reference Documents */}
              <div className="bg-cp-surface2 border-2 border-cp-line rounded-lg p-6">
                <h3 className="text-xl font-bold text-cp-fg mb-4">📚 Complete Patent Documentation</h3>
                <p className="text-sm text-cp-muted mb-4">
                  For complete technical disclosures, detailed algorithms, claim language, and prior art analysis:
                </p>
                <div className="space-y-2">
                  <div className="bg-cp-surface rounded p-3 border border-cp-line">
                    <code className="text-xs bg-cp-bg text-cp-ok px-3 py-2 rounded block mb-2">
                      /docs/PATENTABLE_INNOVATIONS.md
                    </code>
                    <p className="text-xs text-cp-muted">
                      Complete technical descriptions, pseudo-code, prior art distinction for all 7 innovations
                    </p>
                  </div>
                  <div className="bg-cp-surface rounded p-3 border border-cp-line">
                    <code className="text-xs bg-cp-bg text-cp-ok px-3 py-2 rounded block mb-2">
                      /docs/PATENT_DISCLOSURE_ENHANCED.md
                    </code>
                    <p className="text-xs text-cp-muted">
                      USPTO-ready provisional patent application with technical effects, dependent claims, and commercial applications
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </section>

          <section id="user-journeys" className="scroll-mt-4">
            <h1 className="text-3xl font-bold text-cp-fg mb-6 flex items-center gap-3">
              <Map className="h-8 w-8 text-cp-indigo" />
              User Journeys
            </h1>
            <div className="prose max-w-none space-y-6 text-cp-muted">
              <p className="text-lg text-cp-muted">
                Typical user workflows for different personas using Halo platform.
              </p>

              {/* Journey 1: SAP Developer */}
              <div className="bg-gradient-to-r from-cp-inset to-cp-surface border-2 border-cp-line rounded-lg p-6">
                <h3 className="text-2xl font-bold text-cp-fg mb-4">👨‍💻 Journey 1: SAP Developer - Creating Custom Fiori App</h3>
                
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-cp-indigo text-white rounded-full flex items-center justify-center font-bold">1</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-cp-fg">Configure Target System</h4>
                      <p className="text-sm text-cp-muted mt-1">
                        Navigate to <strong>Settings → SAP Landscape</strong>, add S/4HANA system, configure 
                        architecture (Multi-client: DEV=100, QA=200, PROD=300), test connection.
                      </p>
                      <div className="text-xs text-cp-indigo mt-2 bg-cp-surface/50 p-2 rounded">
                        ⏱️ Time: 3-5 minutes (one-time setup)
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-cp-indigo text-white rounded-full flex items-center justify-center font-bold">2</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-cp-fg">Create Ticket</h4>
                      <p className="text-sm text-cp-muted mt-1">
                        Go to <strong>Tickets → Create Ticket</strong>. Title: "Employee Leave Request App". 
                        Description: Detailed requirements with data fields, approval workflow, integration points. 
                        Select target system: RTS S4-D.
                      </p>
                      <div className="text-xs text-cp-indigo mt-2 bg-cp-surface/50 p-2 rounded">
                        ⏱️ Time: 2-3 minutes
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-cp-indigo text-white rounded-full flex items-center justify-center font-bold">3</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-cp-fg">Execute AI Workflow</h4>
                      <p className="text-sm text-cp-muted mt-1">
                        Click <strong>"Workflow"</strong> button. DESIGN agent may ask clarifying questions 
                        (approval levels, data fields). Answer and submit. Watch all 5 agents execute sequentially.
                      </p>
                      <div className="text-xs text-cp-indigo mt-2 bg-cp-surface/50 p-2 rounded">
                        ⏱️ Time: 60-90 seconds (automated)
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-cp-indigo text-white rounded-full flex items-center justify-center font-bold">4</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-cp-fg">Review Generated Code</h4>
                      <p className="text-sm text-cp-muted mt-1">
                        Click <strong>"Code"</strong> tab. Monaco editor shows CAP service with entities, 
                        actions, and business logic. Click <strong>"Show Preview"</strong> to see Fiori UI mockup.
                      </p>
                      <div className="text-xs text-cp-indigo mt-2 bg-cp-surface/50 p-2 rounded">
                        ⏱️ Time: 5-10 minutes (review)
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-cp-indigo text-white rounded-full flex items-center justify-center font-bold">5</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-cp-fg">Deploy to DEV</h4>
                      <p className="text-sm text-cp-muted mt-1">
                        <strong>Transport tab → Mark Code Complete → Complete DEV Testing → Tests Passed</strong>. 
                        System creates transport request (DEVK9A3B4C). Code deployed to DEV environment.
                      </p>
                      <div className="text-xs text-cp-indigo mt-2 bg-cp-surface/50 p-2 rounded">
                        ⏱️ Time: 1-2 minutes (3 clicks)
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-cp-indigo text-white rounded-full flex items-center justify-center font-bold">6</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-cp-fg">Deploy to QA & PROD</h4>
                      <p className="text-sm text-cp-muted mt-1">
                        Navigate to <strong>Deployment Studio → Deploy to QA → Health Check → Deploy to PROD</strong>. 
                        Approve governance review. Application live in production.
                      </p>
                      <div className="text-xs text-cp-indigo mt-2 bg-cp-surface/50 p-2 rounded">
                        ⏱️ Time: 5-10 minutes (including approvals)
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-cp-surface rounded-lg">
                  <strong className="text-cp-fg">Total Time:</strong>
                  <span className="text-cp-muted ml-2">~20-30 minutes (vs. 2-4 weeks manually)</span>
                  <div className="text-sm text-cp-indigo mt-2">
                    Output: Production-ready Fiori app with CAP backend, 95% Clean Core compliant, 
                    automated tests, deployed across 3 environments.
                  </div>
                </div>
              </div>

              {/* Journey 2: System Integrator */}
              <div className="bg-gradient-to-r from-cp-inset to-cp-inset border-2 border-cp-line rounded-lg p-6 mt-6">
                <h3 className="text-2xl font-bold text-cp-ok mb-4">🤝 Journey 2: SI Consultant - Multi-Customer Delivery</h3>
                
                <p className="text-sm text-cp-ok mb-4">
                  System Integrator delivers similar customizations to 5 enterprise clients in parallel.
                </p>

                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-cp-ok text-white rounded-full flex items-center justify-center font-bold">1</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-cp-ok">Create Agent Group</h4>
                      <p className="text-sm text-cp-ok mt-1">
                        <strong>Agent Studio → Agent Groups → Create Group</strong>. Name: "Procurement Team". 
                        Add agents: ABAP Expert, Fiori Expert, Integration Expert. Enable Master Agent with SAP Solution Architect.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-cp-ok text-white rounded-full flex items-center justify-center font-bold">2</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-cp-ok">Bulk Ticket Creation</h4>
                      <p className="text-sm text-cp-ok mt-1">
                        Create 5 tickets for different clients with similar procurement requirements but different system configurations.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-cp-ok text-white rounded-full flex items-center justify-center font-bold">3</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-cp-ok">Parallel Execution</h4>
                      <p className="text-sm text-cp-ok mt-1">
                        Assign "Procurement Team" group to all 5 tickets. Execute workflows in parallel. 
                        Master agent decomposes requirements and delegates to specialists.
                      </p>
                      <div className="text-xs text-cp-ok mt-2 bg-cp-surface/50 p-2 rounded">
                        Result: 5 customizations delivered simultaneously in ~90 seconds each
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-cp-ok text-white rounded-full flex items-center justify-center font-bold">4</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-cp-ok">Quality Review & Delivery</h4>
                      <p className="text-sm text-cp-ok mt-1">
                        Review outputs in <strong>Analytics</strong> dashboard. All agents showing 100% success rate. 
                        Deploy to client systems via Deployment Studio.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-cp-surface rounded-lg">
                  <strong className="text-cp-ok">Consultant Productivity:</strong>
                  <div className="text-sm text-cp-ok mt-2">
                    • <strong>Before Halo:</strong> 1 consultant delivers 1 customization in 2-4 weeks<br/>
                    • <strong>With Halo:</strong> 1 consultant delivers 5 customizations in 1 day<br/>
                    • <strong>Improvement:</strong> 50-100x productivity gain
                  </div>
                </div>
              </div>

              {/* Journey 3: Platform Admin */}
              <div className="bg-cp-inset border-2 border-cp-line rounded-lg p-6 mt-6">
                <h3 className="text-2xl font-bold text-cp-fg mb-4">⚙️ Journey 3: Platform Admin - Discovery & Compliance</h3>
                
                <p className="text-sm text-cp-muted mb-4">
                  SAP Basis Administrator analyzes landscape and ensures Clean Core compliance.
                </p>

                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-cp-violet text-white rounded-full flex items-center justify-center font-bold">1</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-cp-fg">Run SAP Landscape Discovery</h4>
                      <p className="text-sm text-cp-muted mt-1">
                        <strong>Discovery Engine → Select System: S/4HANA Production → Discover SAP Landscape</strong>. 
                        Wait 10-15 seconds. Results show all modules, customizations, Clean Core compliance per module.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-cp-violet text-white rounded-full flex items-center justify-center font-bold">2</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-cp-fg">Review Technical Debt</h4>
                      <p className="text-sm text-cp-muted mt-1">
                        Discovery shows: 34 critical modifications (680h effort), 234 legacy code items. 
                        AI-generated modernization roadmap with 4 phases and $2.4M investment recommendation.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-cp-violet text-white rounded-full flex items-center justify-center font-bold">3</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-cp-fg">Monitor Ongoing Customizations</h4>
                      <p className="text-sm text-cp-muted mt-1">
                        <strong>Dashboard</strong> shows: 12 active tickets, 95% Clean Core compliance maintained. 
                        <strong>Analytics</strong> page shows agent performance trends over time.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-cp-violet text-white rounded-full flex items-center justify-center font-bold">4</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-cp-fg">Governance Approvals</h4>
                      <p className="text-sm text-cp-muted mt-1">
                        <strong>Deployment Studio</strong> shows 3 customizations awaiting PROD approval. 
                        Review governance panel (code quality: 95%, security: passed, tests: passed). Approve all.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-cp-surface rounded-lg">
                  <strong className="text-cp-fg">Compliance Guarantee:</strong>
                  <div className="text-sm text-cp-violet mt-2">
                    Every customization validated for Clean Core compliance before deployment. 
                    Automated governance reduces manual review time from days to minutes.
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="pricing-strategy" className="scroll-mt-4">
            <h1 className="text-3xl font-bold text-cp-fg mb-6 flex items-center gap-3">
              <DollarSign className="h-8 w-8 text-cp-indigo" />
              Pricing Strategy
            </h1>
            <div className="prose max-w-none space-y-6 text-cp-muted">
              <p className="text-lg text-cp-muted">
                Halo offers flexible pricing models tailored to customer segment and usage patterns.
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Tier 1 */}
                <div className="bg-cp-surface border-2 border-cp-line rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-cp-fg">Starter</h3>
                    <div className="text-4xl font-bold text-cp-indigo my-4">$2,500</div>
                    <div className="text-sm text-cp-muted">per month</div>
                  </div>
                  
                  <ul className="space-y-3 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-cp-ok">✓</span>
                      <span><strong>20 customizations</strong> per month</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-ok">✓</span>
                      <span>1 SAP system connection</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-ok">✓</span>
                      <span>5 core agents</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-ok">✓</span>
                      <span>3 custom agents</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-ok">✓</span>
                      <span>Discovery Engine</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-ok">✓</span>
                      <span>Email support</span>
                    </li>
                  </ul>

                  <div className="mt-6 text-center">
                    <div className="text-xs text-cp-faint">Per customization: ~$125</div>
                  </div>
                </div>

                {/* Tier 2 */}
                <div className="bg-gradient-to-br from-cp-indigo to-cp-violet text-white border-2 border-cp-indigo rounded-lg p-6 transform scale-105 shadow-xl">
                  <div className="text-center mb-6">
                    <span className="inline-block px-3 py-1 bg-cp-mod text-cp-mod text-xs font-bold rounded-full mb-2">
                      MOST POPULAR
                    </span>
                    <h3 className="text-2xl font-bold">Professional</h3>
                    <div className="text-4xl font-bold my-4">$10,000</div>
                    <div className="text-sm text-white/80">per month</div>
                  </div>
                  
                  <ul className="space-y-3 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-cp-mod">✓</span>
                      <span><strong>100 customizations</strong> per month</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-mod">✓</span>
                      <span>Up to 5 SAP systems</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-mod">✓</span>
                      <span>5 core agents + customization</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-mod">✓</span>
                      <span><strong>Unlimited</strong> custom agents</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-mod">✓</span>
                      <span>Agent groups & master orchestration</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-mod">✓</span>
                      <span>Priority support (4h response)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-mod">✓</span>
                      <span>Dedicated account manager</span>
                    </li>
                  </ul>

                  <div className="mt-6 text-center">
                    <div className="text-xs text-white/80">Per customization: ~$100</div>
                  </div>
                </div>

                {/* Tier 3 */}
                <div className="bg-cp-surface border-2 border-cp-line rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-cp-fg">Enterprise</h3>
                    <div className="text-3xl font-bold text-cp-violet my-4">Custom</div>
                    <div className="text-sm text-cp-muted">contact sales</div>
                  </div>
                  
                  <ul className="space-y-3 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-cp-violet">✓</span>
                      <span><strong>Unlimited</strong> customizations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-violet">✓</span>
                      <span>Unlimited SAP systems</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-violet">✓</span>
                      <span>Full platform customization</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-violet">✓</span>
                      <span>White-label options</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-violet">✓</span>
                      <span>On-premise deployment option</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-violet">✓</span>
                      <span>24/7 premium support</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cp-violet">✓</span>
                      <span>Custom SLA & training</span>
                    </li>
                  </ul>

                  <div className="mt-6 text-center">
                    <div className="text-xs text-cp-faint">Volume discounts available</div>
                  </div>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-10">Alternative Pricing Models</h3>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-cp-inset border border-cp-line rounded-lg p-6">
                  <h4 className="text-lg font-bold text-cp-fg mb-3">💳 Pay-Per-Customization</h4>
                  <p className="text-sm text-cp-muted mb-4">
                    No monthly commitment. Pay only for customizations delivered.
                  </p>
                  <div className="bg-cp-surface rounded p-4 border border-cp-line">
                    <div className="text-2xl font-bold text-cp-indigo">$150</div>
                    <div className="text-sm text-cp-muted">per customization</div>
                    <ul className="mt-3 space-y-1 text-xs text-cp-muted">
                      <li>• Minimum purchase: 5 customizations</li>
                      <li>• Prepaid credits (no expiration)</li>
                      <li>• Best for: Occasional SAP development needs</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-cp-inset border border-cp-line rounded-lg p-6">
                  <h4 className="text-lg font-bold text-cp-fg mb-3">👥 SI Partner Program</h4>
                  <p className="text-sm text-cp-muted mb-4">
                    For system integrators delivering to multiple clients.
                  </p>
                  <div className="bg-cp-surface rounded p-4 border border-cp-line">
                    <div className="text-2xl font-bold text-cp-ok">$0.10-$0.30</div>
                    <div className="text-sm text-cp-muted">per end-user per month</div>
                    <ul className="mt-3 space-y-1 text-xs text-cp-muted">
                      <li>• Revenue share model</li>
                      <li>• White-label capabilities</li>
                      <li>• Best for: SIs with 10,000+ SAP end users</li>
                    </ul>
                  </div>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-10">ROI Calculator</h3>

              <div className="bg-cp-inset border-2 border-cp-line rounded-lg p-6">
                <h4 className="text-lg font-bold text-cp-fg mb-4">Typical Customer ROI (Professional Plan)</h4>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-semibold text-cp-fg mb-3 text-sm">Traditional Development Costs</h5>
                    <ul className="space-y-2 text-sm text-cp-muted">
                      <li>• Developer rate: $150/hour</li>
                      <li>• Average customization: 80 hours</li>
                      <li>• Cost per customization: <strong>$12,000</strong></li>
                      <li>• 10 customizations/month: <strong>$120,000</strong></li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-semibold text-cp-fg mb-3 text-sm">Halo Costs</h5>
                    <ul className="space-y-2 text-sm text-cp-muted">
                      <li>• Subscription: $10,000/month</li>
                      <li>• 10 customizations delivered</li>
                      <li>• Cost per customization: <strong>$1,000</strong></li>
                      <li>• Monthly total: <strong>$10,000</strong></li>
                    </ul>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-cp-surface rounded-lg">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-3xl font-bold text-cp-ok">$110K</div>
                      <div className="text-xs text-cp-muted">Monthly savings</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-cp-ok">92%</div>
                      <div className="text-xs text-cp-muted">Cost reduction</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-cp-ok">11x</div>
                      <div className="text-xs text-cp-muted">ROI multiplier</div>
                    </div>
                  </div>
                  <p className="text-xs text-center text-cp-muted mt-4">
                    Annual savings: <strong className="text-cp-ok">$1.32M</strong>
                  </p>
                </div>
              </div>

              <div className="bg-cp-inset border-l-4 border-cp-mod p-6 mt-8">
                <h4 className="text-lg font-bold text-cp-mod mb-2">💰 Pricing Philosophy</h4>
                <p className="text-cp-mod text-sm">
                  Halo pricing is designed to deliver <strong>immediate positive ROI</strong>. Even at the highest tier, 
                  customers save 90%+ compared to traditional development costs while achieving 10x faster delivery and 
                  guaranteed Clean Core compliance.
                </p>
              </div>
            </div>
          </section>

          <section id="technical-architecture" className="scroll-mt-4">
            <h1 className="text-3xl font-bold text-cp-fg mb-6 flex items-center gap-3">
              <Code className="h-8 w-8 text-cp-indigo" />
              Technical Architecture
            </h1>
            <div className="prose max-w-none space-y-6 text-cp-muted">
              <p className="text-lg text-cp-muted">
                Halo is built on a modern, scalable tech stack optimized for AI-powered enterprise software development.
              </p>

              <h3 className="text-2xl font-semibold text-cp-fg">High-Level System Architecture</h3>

              <div className="bg-cp-bg text-cp-ok p-6 rounded-lg font-mono text-xs overflow-x-auto">
                <pre>{`
┌─────────────────────────────────────────────────────────────────┐
│                     React Frontend (Port 3000)                  │
│  ┌──────────┬──────────┬──────────┬───────────┬──────────────┐ │
│  │Dashboard │ Tickets  │  Agent   │Discovery  │ Deployment   │ │
│  │          │          │  Studio  │  Engine   │   Studio     │ │
│  └──────────┴──────────┴──────────┴───────────┴──────────────┘ │
└───────────────────────────┬─────────────────────────────────────┘
                            │ REST API (axios)
                            ↓
┌─────────────────────────────────────────────────────────────────┐
│                FastAPI Backend (Port 8001)                      │
│  ┌──────────────────────────────────────────────────────────┐  │
│  │  API Routes (/api/*)                                     │  │
│  │  - Tickets    - Agents     - Vibe Sessions              │  │
│  │  - Discovery  - Transport  - Settings                   │  │
│  └──────────────────────────────────────────────────────────┘  │
│  ┌──────────────────────────────────────────────────────────┐  │
│  │  Services                                                │  │
│  │  - AgentService          - CustomAgentService           │  │
│  │  - InteractiveAgentService                              │  │
│  │  - SystemDiscoveryService - SAPLandscapeDiscovery       │  │
│  └──────────────────────────────────────────────────────────┘  │
└───────────────────────────┬─────────────────────────────────────┘
                            │
                            ↓
┌─────────────────────────────────────────────────────────────────┐
│                    MongoDB Database                             │
│  Collections: tickets, agent_tasks, custom_agents,              │
│  vibe_sessions, transport_lifecycles, code_generations          │
└─────────────────────────────────────────────────────────────────┘
                            │
                            ↓
┌─────────────────────────────────────────────────────────────────┐
│              External Integrations                              │
│  - OpenAI (GPT-4o, GPT-5, o1)                                  │
│  - Anthropic (Claude 4)                                         │
│  - Google (Gemini 2.5)                                          │
│  - SAP Systems (OData, REST, Cloud Foundry)                     │
└─────────────────────────────────────────────────────────────────┘
                `}</pre>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-10">Technology Stack</h3>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-cp-surface border border-cp-line rounded-lg p-6">
                  <h4 className="text-lg font-bold text-cp-fg mb-4">Frontend</h4>
                  <ul className="space-y-2 text-sm text-cp-muted">
                    <li><strong>Framework:</strong> React 18.2.0</li>
                    <li><strong>Routing:</strong> React Router v6</li>
                    <li><strong>Styling:</strong> Tailwind CSS 3.x + shadcn/ui</li>
                    <li><strong>Code Editor:</strong> Monaco Editor (VS Code engine)</li>
                    <li><strong>Charts:</strong> Recharts</li>
                    <li><strong>HTTP Client:</strong> Axios</li>
                    <li><strong>Icons:</strong> Lucide React</li>
                    <li><strong>Build:</strong> Create React App with CRACO</li>
                  </ul>
                </div>

                <div className="bg-cp-surface border border-cp-line rounded-lg p-6">
                  <h4 className="text-lg font-bold text-cp-fg mb-4">Backend</h4>
                  <ul className="space-y-2 text-sm text-cp-muted">
                    <li><strong>Framework:</strong> FastAPI 0.104+</li>
                    <li><strong>Language:</strong> Python 3.11+</li>
                    <li><strong>Validation:</strong> Pydantic v2</li>
                    <li><strong>Database Driver:</strong> Motor (async MongoDB)</li>
                    <li><strong>LLM Integration:</strong> emergentintegrations</li>
                    <li><strong>Server:</strong> Uvicorn (ASGI)</li>
                    <li><strong>Async:</strong> Full asyncio support</li>
                    <li><strong>API Docs:</strong> Auto-generated OpenAPI/Swagger</li>
                  </ul>
                </div>

                <div className="bg-cp-surface border border-cp-line rounded-lg p-6">
                  <h4 className="text-lg font-bold text-cp-fg mb-4">Database</h4>
                  <ul className="space-y-2 text-sm text-cp-muted">
                    <li><strong>Database:</strong> MongoDB 5.0+</li>
                    <li><strong>Schema:</strong> 13 collections</li>
                    <li><strong>Pattern:</strong> Document-based with references</li>
                    <li><strong>Indexes:</strong> Optimized for common queries</li>
                    <li><strong>Backup:</strong> Automated snapshots</li>
                  </ul>
                </div>

                <div className="bg-cp-surface border border-cp-line rounded-lg p-6">
                  <h4 className="text-lg font-bold text-cp-fg mb-4">Infrastructure</h4>
                  <ul className="space-y-2 text-sm text-cp-muted">
                    <li><strong>Container:</strong> Docker</li>
                    <li><strong>Orchestration:</strong> Kubernetes</li>
                    <li><strong>Process Manager:</strong> Supervisor</li>
                    <li><strong>Proxy:</strong> Nginx</li>
                    <li><strong>Environment:</strong> Cloud-based (AWS/Azure/GCP)</li>
                  </ul>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-10">Key Architectural Patterns</h3>

              <div className="space-y-4">
                <div className="bg-cp-inset border-l-4 border-cp-indigo p-4">
                  <h4 className="font-bold text-cp-fg mb-2">1. Service Layer Pattern</h4>
                  <p className="text-sm text-cp-muted">
                    Business logic encapsulated in service classes (AgentService, CustomAgentService, etc.). 
                    Controllers are thin, services contain all domain logic.
                  </p>
                </div>

                <div className="bg-cp-inset border-l-4 border-cp-ok p-4">
                  <h4 className="font-bold text-cp-ok mb-2">2. Shared Memory Pattern</h4>
                  <p className="text-sm text-cp-ok">
                    Agents communicate via MongoDB collections. Each agent writes outputs to database, 
                    next agent reads previous outputs. No direct agent-to-agent communication.
                  </p>
                </div>

                <div className="bg-cp-inset border-l-4 border-cp-violet p-4">
                  <h4 className="font-bold text-cp-fg mb-2">3. Async-First Architecture</h4>
                  <p className="text-sm text-cp-muted">
                    All I/O operations (LLM calls, database queries) use async/await. Enables high concurrency 
                    and non-blocking operations for improved performance.
                  </p>
                </div>

                <div className="bg-cp-inset border-l-4 border-cp-imp p-4">
                  <h4 className="font-bold text-cp-imp mb-2">4. Polymorphic Deployer Pattern</h4>
                  <p className="text-sm text-cp-imp">
                    Abstract base deployer with concrete implementations for each SAP deployment method 
                    (Cloud Foundry, CTS+, ADT, OData). Factory pattern selects appropriate deployer based on 
                    SAP system configuration.
                  </p>
                </div>
              </div>

              <div className="bg-cp-surface2 border border-cp-line rounded-lg p-6 mt-8">
                <h4 className="text-lg font-bold text-cp-fg mb-3">📚 Detailed Technical Documentation</h4>
                <p className="text-sm text-cp-muted mb-4">
                  For complete technical architecture details including database schema, API reference, and deployment specifications, see:
                </p>
                <code className="text-xs bg-cp-bg text-cp-ok px-3 py-2 rounded block">
                  /docs/TECHNICAL_ARCHITECTURE.md
                </code>
                <p className="text-xs text-cp-muted mt-3">
                  Includes: Database schema, API endpoints, service architecture, security specifications, 
                  performance metrics, and scaling considerations.
                </p>
              </div>
            </div>
          </section>

          <section id="product-guide" className="scroll-mt-4">
            <h1 className="text-3xl font-bold text-cp-fg mb-6 flex items-center gap-3">
              <BookMarked className="h-8 w-8 text-cp-indigo" />
              Product Guide
            </h1>
            <div className="prose max-w-none space-y-6 text-cp-muted">
              <p className="text-lg text-cp-muted">
                Comprehensive guide to all Halo features, capabilities, and workflows.
              </p>

              <div className="bg-cp-inset border border-cp-line rounded-lg p-6">
                <h3 className="text-xl font-bold text-cp-fg mb-3">📖 Complete Product Guide</h3>
                <p className="text-cp-muted text-sm mb-4">
                  The full product guide includes detailed documentation on:
                </p>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <ul className="space-y-2 text-cp-muted">
                    <li>• Getting Started & Configuration</li>
                    <li>• Dashboard Overview</li>
                    <li>• Ticket Management</li>
                    <li>• Multi-Agent Orchestration</li>
                    <li>• Agent Studio & Custom Agents</li>
                    <li>• Chat Studio</li>
                  </ul>
                  <ul className="space-y-2 text-cp-muted">
                    <li>• Discovery Engine</li>
                    <li>• Deployment Studio</li>
                    <li>• SAP Landscape Configuration</li>
                    <li>• Transport Lifecycle Management</li>
                    <li>• Analytics & Reporting</li>
                    <li>• Troubleshooting & Best Practices</li>
                  </ul>
                </div>
                <div className="mt-6 p-4 bg-cp-surface rounded">
                  <strong className="text-cp-fg">Access Complete Guide:</strong>
                  <code className="text-xs bg-cp-bg text-cp-ok px-3 py-2 rounded block mt-2">
                    /docs/PRODUCT_GUIDE.md
                  </code>
                  <code className="text-xs bg-cp-bg text-cp-ok px-3 py-2 rounded block mt-2">
                    /docs/Halo_PRODUCT_GUIDE_COMPLETE.md
                  </code>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-cp-fg mt-8">Quick Start Guide</h3>

              <div className="bg-cp-inset border border-cp-line rounded-lg p-6">
                <h4 className="font-bold text-cp-fg mb-4">First 30 Minutes with Halo</h4>
                
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-cp-indigo text-white rounded-full flex items-center justify-center font-bold text-sm">1</span>
                    <div>
                      <strong className="text-cp-fg">Configure SAP System (5 mins)</strong>
                      <p className="text-sm text-cp-muted mt-1">
                        Settings → SAP Landscape → Add system → Complete 5 tabs → Test connection → Verify "Deploy Ready"
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-cp-indigo text-white rounded-full flex items-center justify-center font-bold text-sm">2</span>
                    <div>
                      <strong className="text-cp-fg">Create First Ticket (3 mins)</strong>
                      <p className="text-sm text-cp-muted mt-1">
                        Tickets → Create → Title: "Test Report" → Description: Sales data report → Select target system
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-cp-indigo text-white rounded-full flex items-center justify-center font-bold text-sm">3</span>
                    <div>
                      <strong className="text-cp-fg">Execute Workflow (2 mins)</strong>
                      <p className="text-sm text-cp-muted mt-1">
                        Open ticket → Click "Workflow" → Watch 5 agents execute → Review generated code
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-cp-indigo text-white rounded-full flex items-center justify-center font-bold text-sm">4</span>
                    <div>
                      <strong className="text-cp-fg">Deploy to QA (5 mins)</strong>
                      <p className="text-sm text-cp-muted mt-1">
                        Transport tab → Mark Complete → Create Transport → Deployment Studio → Deploy to QA
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-cp-inset border border-cp-line rounded">
                  <strong className="text-cp-ok">Result:</strong>
                  <span className="text-cp-ok text-sm ml-2">
                    Fully functional SAP customization delivered in ~15 minutes!
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section id="user-guide" className="scroll-mt-4">
            <h1 className="text-3xl font-bold text-cp-fg mb-6 flex items-center gap-3">
              <User className="h-8 w-8 text-cp-indigo" />
              User Guide
            </h1>
            <div className="prose max-w-none space-y-6 text-cp-muted">
              <p className="text-lg text-cp-muted">
                Step-by-step instructions for common tasks and workflows in Halo.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-cp-surface border border-cp-line rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="text-lg font-bold text-cp-fg mb-3">🎫 Creating Tickets</h3>
                  <ol className="space-y-2 text-sm text-cp-muted">
                    <li>1. Navigate to <strong>Tickets</strong> page</li>
                    <li>2. Click <strong>"Create Ticket"</strong> button</li>
                    <li>3. Fill in ticket details:
                      <ul className="ml-4 mt-1 text-xs">
                        <li>- Ticket number, title, description</li>
                        <li>- Priority level</li>
                        <li>- Target SAP system</li>
                        <li>- Customization type</li>
                      </ul>
                    </li>
                    <li>4. Click <strong>"Submit"</strong></li>
                    <li>5. Ticket appears in list</li>
                  </ol>
                </div>

                <div className="bg-cp-surface border border-cp-line rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="text-lg font-bold text-cp-fg mb-3">🤖 Running Workflows</h3>
                  <ol className="space-y-2 text-sm text-cp-muted">
                    <li>1. Open ticket from list</li>
                    <li>2. Click <strong>"Workflow"</strong> button</li>
                    <li>3. If agent asks questions:
                      <ul className="ml-4 mt-1 text-xs">
                        <li>- Answer questions</li>
                        <li>- Click "Submit Answer"</li>
                      </ul>
                    </li>
                    <li>4. Watch agents execute automatically</li>
                    <li>5. Review outputs in Activity Stream</li>
                  </ol>
                </div>

                <div className="bg-cp-surface border border-cp-line rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="text-lg font-bold text-cp-fg mb-3">✨ Creating Custom Agents</h3>
                  <ol className="space-y-2 text-sm text-cp-muted">
                    <li>1. Go to <strong>Agent Studio</strong></li>
                    <li>2. Click <strong>"Create Custom Agent"</strong></li>
                    <li>3. Enter agent details:
                      <ul className="ml-4 mt-1 text-xs">
                        <li>- Name and description</li>
                        <li>- Category</li>
                        <li>- System prompt (expertise)</li>
                        <li>- AI model selection</li>
                        <li>- Temperature and tokens</li>
                      </ul>
                    </li>
                    <li>4. Click <strong>"Create Agent"</strong></li>
                    <li>5. Agent available for ticket assignment</li>
                  </ol>
                </div>

                <div className="bg-cp-surface border border-cp-line rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="text-lg font-bold text-cp-fg mb-3">🚀 Deploying to Production</h3>
                  <ol className="space-y-2 text-sm text-cp-muted">
                    <li>1. Complete DEV and QA testing</li>
                    <li>2. Go to <strong>Deployment Studio</strong></li>
                    <li>3. Find ticket in "Ready for PROD"</li>
                    <li>4. Click <strong>"Request PROD Approval"</strong></li>
                    <li>5. Review governance panel</li>
                    <li>6. Add approval comments</li>
                    <li>7. Click <strong>"Approve & Deploy"</strong></li>
                    <li>8. Ticket deployed to production ✅</li>
                  </ol>
                </div>

                <div className="bg-cp-surface border border-cp-line rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="text-lg font-bold text-cp-fg mb-3">🔍 Running Discovery</h3>
                  <ol className="space-y-2 text-sm text-cp-muted">
                    <li>1. Navigate to <strong>Discovery Engine</strong></li>
                    <li>2. Select SAP system from dropdown</li>
                    <li>3. Click <strong>"Discover SAP Landscape"</strong></li>
                    <li>4. Wait 10-15 seconds for analysis</li>
                    <li>5. Review results:
                      <ul className="ml-4 mt-1 text-xs">
                        <li>- Landscape overview</li>
                        <li>- Module details</li>
                        <li>- Technical debt analysis</li>
                        <li>- Modernization roadmap</li>
                      </ul>
                    </li>
                  </ol>
                </div>

                <div className="bg-cp-surface border border-cp-line rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="text-lg font-bold text-cp-fg mb-3">⚙️ Configuring SAP Systems</h3>
                  <ol className="space-y-2 text-sm text-cp-muted">
                    <li>1. Go to <strong>Settings → SAP Landscape</strong></li>
                    <li>2. Click <strong>"Add SAP System"</strong></li>
                    <li>3. Complete all 5 tabs:
                      <ul className="ml-4 mt-1 text-xs">
                        <li>- Basic Info</li>
                        <li>- Architecture (critical!)</li>
                        <li>- Access & APIs</li>
                        <li>- Credentials</li>
                        <li>- Transport settings</li>
                      </ul>
                    </li>
                    <li>4. Click <strong>"Save Configuration"</strong></li>
                    <li>5. Verify "Deploy Ready" badge appears</li>
                  </ol>
                </div>
              </div>

              <div className="bg-cp-inset border-l-4 border-cp-indigo p-6 mt-8">
                <h3 className="text-lg font-bold text-cp-fg mb-3">💡 Pro Tips</h3>
                <ul className="space-y-2 text-sm text-cp-muted">
                  <li>• <strong>Detailed Descriptions:</strong> More detail in ticket description = better AI output</li>
                  <li>• <strong>Custom Agents:</strong> Create domain-specific agents for recurring customization types</li>
                  <li>• <strong>Agent Groups:</strong> Use groups with master orchestration for complex multi-domain tickets</li>
                  <li>• <strong>Health Checks:</strong> Always run health checks before QA and PROD deployment</li>
                  <li>• <strong>Chat Studio:</strong> Use for iterative refinement and code explanation</li>
                </ul>
              </div>

              <div className="bg-cp-surface2 border border-cp-line rounded-lg p-6 mt-8">
                <h4 className="text-lg font-bold text-cp-fg mb-3">📚 Additional Resources</h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <strong className="text-cp-fg">Product Documentation:</strong>
                    <ul className="mt-2 space-y-1 text-cp-muted">
                      <li>• <code className="text-xs bg-cp-surface px-2 py-1 rounded">/docs/PRODUCT_GUIDE.md</code></li>
                      <li>• <code className="text-xs bg-cp-surface px-2 py-1 rounded">/docs/Halo_PRODUCT_GUIDE_COMPLETE.md</code></li>
                    </ul>
                  </div>
                  <div>
                    <strong className="text-cp-fg">Technical Documentation:</strong>
                    <ul className="mt-2 space-y-1 text-cp-muted">
                      <li>• <code className="text-xs bg-cp-surface px-2 py-1 rounded">/docs/TECHNICAL_ARCHITECTURE.md</code></li>
                      <li>• <code className="text-xs bg-cp-surface px-2 py-1 rounded">/docs/Halo_TECHNICAL_ARCHITECTURE_COMPLETE.md</code></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default Documentation;
